import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { AgGridReact } from "ag-grid-react";
import type { CellClickedEvent, ColDef, ColGroupDef, FilterChangedEvent, GridApi, GridReadyEvent, ICellRendererParams, ModelUpdatedEvent, ValueFormatterParams } from "ag-grid-community";
import { themeQuartz } from "ag-grid-community";
import { useQuery } from "@tanstack/react-query";

import { formatProjectActivityCount, isPendingProjectInvite, projectActivityStats, type Project } from "../../api/projects";
import { fetchPlot, fetchPlots, type SavedPlot } from "../../api/plots";
import type { ThreadChatMessage, ThreadDashboardEditingEvent, ThreadPresenceState, ThreadRealtimeStatus, ThreadTypingEvent } from "../../api/realtime";
import type { ThreadV4LayoutJson, ThreadV4NotebookEntry, ThreadV4ViewSpec, ThreadV4WidgetKind, ThreadV4WidgetPosition, ThreadV4WidgetSpec } from "../../api/threadV4Views";
import { ThreadLogo } from "../../components/ThreadLogo";
import type { CorrelogramColumn } from "../../components/data-explorer/Correlogram";
import { rankColumnsByCorrelationTo } from "../../components/data-explorer/correlationRanking";
import { FeedbackDevBadge } from "../../components/feedback/FeedbackReporter";
import { MembersPanel } from "../../components/members/MembersPanel";
import { createDefaultConfig, type PlotBuilderConfig, type PlotEngine } from "../../components/plot-builder/types";
import { getRenderer } from "../../components/plot-builder/renderers";
import { DataPointContextMenu, useDataPointContextMenu } from "../../components/shared/DataPointContextMenu";
import Flow360ViewerPanel from "../../components/panels/Flow360ViewerPanel";
import type { ThreadAsset, ThreadColumn, ThreadRow } from "../thread-design-system";
import type { V3WorkbenchData } from "../thread-v3/types";
import { Icons } from "../thread-v4/icons";
import { formatValue, rowKey, rowState } from "../thread-v4/ThreadV4Widgets";
import { ThreadV5BundleProvenance, ThreadV5DagMinimap } from "./ThreadV5Provenance";
import { ConditionPivotTable } from "./ThreadV5ConditionPivotTable";
import { ThreadV5LineageExplorer } from "./ThreadV5LineageExplorer";
import type { V5FlowfieldPair, V5Focus, V5TableMode, V5View } from "./ThreadV5Workbench";
import { useDashboardLayoutCollaboration } from "./useV5Realtime";

const DataExplorerCorrelogram = lazy(() =>
  import("../../components/data-explorer/Correlogram").then((module) => ({ default: module.Correlogram })),
);

const V5_CORRELOGRAM_GROUP_COLORS = ["#4f9f77", "#b48243", "#6f8fc7", "#9a72b8", "#c56f5d", "#4b9aa7", "#8d9460", "#c27c95"];
const V5_CORRELOGRAM_MAX_COLUMNS = 12;
const V5_CORRELOGRAM_RESPONSE_PRIORITY = [
  "cl",
  "liftcoefficient",
  "cm",
  "cmpitch",
  "cd",
  "dragcoefficient",
  "cy",
  "cfz",
  "ld",
  "liftodrag",
];
const WORKBENCH_LAYOUT_STORAGE_KEY = "thread:v5:workbench:layout";
const DEFAULT_WORKBENCH_LAYOUT = { rightWidth: 320, bottomHeight: 128 };
const RIGHT_WIDTH_BOUNDS = { min: 240, max: 560 };
const BOTTOM_HEIGHT_BOUNDS = { min: 104, max: 360 };
const DASHBOARD_GRID_GAP = 10;
const DASHBOARD_MIN_WIDGET = { w: 2, h: 1 };

type SelectRunHandler = (runId: string, nodeId?: string | null) => void;
type V5GridColDef = ColDef<ThreadRow>;
type V5GridColumnDef = V5GridColDef | ColGroupDef<ThreadRow>;

export interface DashboardSaveRequest {
  name: string;
  layout_json: ThreadV4LayoutJson;
  binding_json?: Record<string, unknown>;
  widget_state_json?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  sourceViewId?: string | null;
  updateExisting?: boolean;
}

interface ShellProps {
  projectName: string;
  branch: string | null;
  connected: boolean;
  selectedRunId: string | null;
  activeDashboardName: string | null;
  notebookOpen: boolean;
  userInitials: string;
  flow360Connector?: Flow360ConnectorSummary;
  presenceStatus?: ThreadRealtimeStatus;
  presenceUsers?: ThreadPresenceState[];
  chatOpen?: boolean;
  chatUnreadCount?: number;
  onClearSelection: () => void;
  onOpenBranching: () => void;
  onOpenFlow360: () => void;
  onOpenNotebook: () => void;
  onOpenChat?: () => void;
  onOpenMembers: () => void;
  onOpenProjects: () => void;
  onCreateDashboard: () => void;
}

export interface Flow360ConnectorSummary {
  connected: boolean;
  browserSession: boolean;
  storedKey: boolean;
  email: string | null;
  statusLabel: string;
  detail: string;
}

interface ProjectsSurfaceProps {
  projects: Project[];
  loading: boolean;
  error: string | null;
  currentProjectId: string | null;
  onOpenProject: (project: Project) => void;
  onAcceptInvitation: (project: Project) => void;
  onRefresh: () => void;
  onOpenFlow360: () => void;
  acceptingProjectId?: string | null;
  acceptError?: string | null;
}

export interface SurfaceProps {
  data: V3WorkbenchData;
  dataLoading: boolean;
  projectId: string;
  projectName: string;
  branch: string;
  persistedProject: boolean;
  selectedRunId: string | null;
  selectedNodeId: string | null;
  focusedAssetId: string | null;
  tableMode: V5TableMode;
  activeDashboardName: string | null;
  currentView?: ThreadV4ViewSpec | null;
  views: ThreadV4ViewSpec[];
  notebookEntries: ThreadV4NotebookEntry[];
  onTableModeChange: (mode: V5TableMode) => void;
  onSelectRun: SelectRunHandler;
  onOpenArtifact: (assetId: string) => void;
  onOpenFocus: (focus: V5Focus) => void;
  onOpenFlow360: () => void;
  onOpenView: (view: V5View) => void;
  onOpenDashboard: (view: ThreadV4ViewSpec) => void;
  onSaveDashboard: (request: DashboardSaveRequest) => void;
}

interface NotebookProps {
  selectedRunId: string | null;
  entries: ThreadV4NotebookEntry[];
  draft: string;
  onDraftChange: (value: string) => void;
  onAdd: () => void;
  onClose: () => void;
}

type ProjectChatStatus = ThreadRealtimeStatus | "loading" | "ready";

interface ProjectChatDrawerProps {
  projectName: string;
  status: ProjectChatStatus;
  streamStatus: ThreadRealtimeStatus;
  messages: ThreadChatMessage[];
  draft: string;
  sending: boolean;
  typingUsers: ThreadTypingEvent[];
  error: string | null;
  onDraftChange: (value: string) => void;
  onTyping: () => void;
  onSend: () => Promise<void>;
  onClose: () => void;
}

function identityInitials(identity: string | null | undefined): string {
  const trimmed = (identity ?? "").trim();
  if (!trimmed) return "??";
  const label = trimmed.includes("@") ? trimmed.split("@")[0] ?? trimmed : trimmed;
  const parts = label.split(/[\s._-]+/).filter(Boolean);
  const letters = parts.length > 1
    ? `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`
    : label.slice(0, 2);
  return (letters || "??").toUpperCase();
}

function presenceLabel(connected: boolean, status: ThreadRealtimeStatus): string {
  if (status === "connected") return "collaborators live";
  if (status === "connecting") return "collaborators connecting";
  if (status === "auth-missing") return connected ? "collaboration locked" : "connecting";
  if (status === "error") return connected ? "collaboration unavailable" : "connecting";
  return connected ? "backend live" : "connecting";
}

function chatStatusLabel(status: ProjectChatStatus, streamStatus: ThreadRealtimeStatus): string {
  if (status === "loading") return "loading chat";
  if (status === "error") return "chat offline";
  if (status === "auth-missing" || streamStatus === "auth-missing") return "chat locked";
  if (status === "ready" && streamStatus === "connected") return "live chat";
  if (status === "ready" && streamStatus === "connecting") return "chat connecting";
  if (status === "ready") return "chat ready";
  return "chat idle";
}

function chatTimeLabel(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" }).format(date);
}

export function ThreadV5Shell({
  projectName,
  branch,
  connected,
  selectedRunId,
  activeDashboardName,
  notebookOpen,
  userInitials,
  flow360Connector,
  presenceStatus = "idle",
  presenceUsers = [],
  chatOpen = false,
  chatUnreadCount = 0,
  onClearSelection,
  onOpenBranching,
  onOpenFlow360,
  onOpenNotebook,
  onOpenChat,
  onOpenMembers,
  onOpenProjects,
  onCreateDashboard,
}: ShellProps) {
  const visiblePresenceUsers = presenceUsers.slice(0, 3);
  const remainingPresenceUsers = Math.max(0, presenceUsers.length - visiblePresenceUsers.length);
  const liveClassName = presenceStatus === "connected" ? "is-connected" : "is-pending";

  return (
    <header className="v5-topbar">
      <button className="v5-brand" type="button" onClick={onOpenProjects} aria-label="Open Thread projects">
        <ThreadLogo height={23} />
        <span className="v5-brand-version">V5</span>
      </button>
      <div className="v5-crumb">
        <span className="v5-project-name">{projectName}</span>
        {branch ? <button className="v5-branch-chip" type="button" onClick={onOpenBranching}><Icons.Branch size={13} />{branch}</button> : null}
        {selectedRunId ? (
          <>
            <span className="v5-crumb-sep">/</span>
            <span className="v5-run-crumb">run {selectedRunId}</span>
            <button className="v5-clear-run" type="button" onClick={onClearSelection} aria-label="Clear selected run"><Icons.Close size={11} /></button>
          </>
        ) : null}
      </div>
      <span className={`v5-live ${liveClassName}`}><i />{presenceLabel(connected, presenceStatus)}</span>
      {activeDashboardName ? <span className="v5-dashboard-chip">{activeDashboardName}</span> : null}
      <FeedbackDevBadge />
      <div className="v5-topbar-spacer" />
      {visiblePresenceUsers.length ? (
        <div className="v5-presence-stack" aria-label={`${presenceUsers.length} collaborators present`}>
          {visiblePresenceUsers.map((state) => {
            const displayName = state.display_name || state.user_id;
            return (
              <span key={state.user_id} className="v5-presence-avatar" title={displayName}>
                {identityInitials(displayName)}
              </span>
            );
          })}
          {remainingPresenceUsers ? <span className="v5-presence-avatar is-count">+{remainingPresenceUsers}</span> : null}
        </div>
      ) : null}
      <label className="v5-search">
        <Icons.Search size={13} />
        <input placeholder="Search runs, artifacts, notes..." />
        <kbd>/</kbd>
      </label>
      <button
        className={`v5-connector-chip ${flow360Connector?.connected ? "is-connected" : ""}`}
        type="button"
        onClick={onOpenFlow360}
        title={flow360Connector?.detail ?? "Open Flow360 connector"}
      >
        <Icons.Flowfield size={13} />
        <span>Flow360</span>
        <em>{flow360Connector?.statusLabel ?? "connector"}</em>
      </button>
      {onOpenChat ? (
        <button className={`v5-icon-button v5-chat-button ${chatOpen ? "is-active" : ""}`} type="button" aria-label="Open project chat" title="Project chat" onClick={onOpenChat}>
          <Icons.Comments size={15} />
          {chatUnreadCount ? <span className="v5-chat-badge">{Math.min(chatUnreadCount, 99)}</span> : null}
        </button>
      ) : null}
      <button className={`v5-icon-button ${notebookOpen ? "is-active" : ""}`} type="button" title="Spool" onClick={onOpenNotebook}><Icons.MdNote size={15} /></button>
      <button className="v5-text-button" type="button" onClick={onCreateDashboard}><Icons.Plus size={13} />Dashboard</button>
      <button className="v5-avatar-button" type="button" aria-label="Open project members" title="Project members" onClick={onOpenMembers}>
        <span className="v5-avatar">{userInitials}</span>
      </button>
    </header>
  );
}

export function ThreadV5NavRail({ activeView, dashboards, onOpenView, onOpenDashboard }: { activeView: V5View; dashboards: ThreadV4ViewSpec[]; onOpenView: (view: V5View) => void; onOpenDashboard: (view: ThreadV4ViewSpec) => void }) {
  const item = (view: V5View, label: string, icon: ReactNode) => (
    <button className={`v5-rail-item ${activeView === view ? "is-active" : ""}`} type="button" title={label} onClick={() => onOpenView(view)}>
      {icon}
      <span>{label}</span>
    </button>
  );
  return (
    <nav className="v5-rail" aria-label="Thread V5 surfaces">
      {item("projects", "Projects", <Icons.Project size={15} />)}
      {item("workbench", "Workbench", <Icons.GridIcon size={15} />)}
      {item("home", "Home", <Icons.Project size={15} />)}
      {item("branching", "Branching", <Icons.Branch size={15} />)}
      <span className="v5-rail-label">Data</span>
      {item("data", "Data", <Icons.Table size={15} />)}
      {item("filament-model", "Filament Model", <Icons.Flowfield size={15} />)}
      {item("tailor", "ADB Generator", <Icons.GridIcon size={15} />)}
      {item("artifacts", "Artifacts", <Icons.Asset size={15} />)}
      {item("provenance", "Provenance", <Icons.Dag size={15} />)}
      {/* FXC-8630: Lineage Graph — first-class nav entry for the full Warp lineage surface */}
      {item("lineage", "Lineage", <Icons.Dag size={15} />)}
      <span className="v5-rail-label">Boards</span>
      {dashboards.slice(0, 4).map((view) => (
        <button key={view.id} className="v5-rail-item" type="button" title={view.name} onClick={() => onOpenDashboard(view)}>
          <Icons.SavedPlots size={15} />
          <span>{view.name}</span>
        </button>
      ))}
      <button className={`v5-rail-item ${activeView === "dashboard-edit" ? "is-active" : ""}`} type="button" title="New dashboard" onClick={() => onOpenView("dashboard-edit")}>
        <Icons.Plus size={15} />
        <span>New</span>
      </button>
    </nav>
  );
}

export function ProjectsSurface({
  projects,
  loading,
  error,
  currentProjectId,
  onOpenProject,
  onAcceptInvitation,
  onRefresh,
  onOpenFlow360,
  acceptingProjectId,
  acceptError,
}: ProjectsSurfaceProps) {
  return (
    <main className="v5-main v5-projects">
      <header className="v5-page-header v5-projects-header">
        <div>
          <span className="v5-eyebrow">Projects</span>
          <h1>Thread projects</h1>
          <p>Open a live project, reconnect Flow360, or jump back into the current workbench.</p>
        </div>
        <div className="v5-project-actions">
          <button className="v5-text-button" type="button" onClick={onRefresh} disabled={loading}><Icons.Refresh size={13} />Refresh</button>
          <button className="v5-primary-button" type="button" onClick={onOpenFlow360}><Icons.Flowfield size={14} />Flow360</button>
        </div>
      </header>
      {acceptError ? (
        <div className="v5-project-browser-state is-error"><Icons.Info size={22} /><strong>Invitation unavailable</strong><p>{acceptError}</p></div>
      ) : null}
      {loading ? (
        <div className="v5-project-browser-state"><Icons.Refresh size={22} /><strong>Loading projects</strong></div>
      ) : error ? (
        <div className="v5-project-browser-state is-error"><Icons.Info size={22} /><strong>Projects unavailable</strong><p>{error}</p></div>
      ) : projects.length ? (
        <div className="v5-project-browser">
          {projects.map((project) => {
            const active = project.id === currentProjectId;
            const pendingInvite = isPendingProjectInvite(project);
            const accepting = acceptingProjectId === project.id;
            const flow360Links = project.flow360_links ?? [];
            const activityStats = projectActivityStats(project);
            const cardClassName = [
              "v5-project-card",
              active ? "is-active" : "",
              pendingInvite ? "is-pending-invite" : "",
            ].filter(Boolean).join(" ");
            return (
              <button
                key={project.id}
                className={cardClassName}
                type="button"
                disabled={accepting}
                onClick={() => pendingInvite ? onAcceptInvitation(project) : onOpenProject(project)}
              >
                <span className="v5-project-card-icon"><Icons.Project size={18} /></span>
                <span className="v5-project-card-copy">
                  <strong>{project.name || "Thread project"}</strong>
                  <small>{pendingInvite ? "You have been invited to this project" : project.description || projectMetaLabel(project)}</small>
                  {flow360Links.length > 0 ? (
                    <span className="v5-project-flow360-links">
                      {flow360ProjectNames(project)}
                    </span>
                  ) : null}
                  {activityStats.length > 0 ? (
                    <span className="v5-project-activity-summary" aria-label={`${project.name} activity summary`}>
                      {activityStats.map((stat) => <span key={stat}>{stat}</span>)}
                    </span>
                  ) : null}
                </span>
                {pendingInvite ? <em>{accepting ? "Accepting" : "Accept invite"}</em> : active ? <em>Current</em> : null}
                <span className="v5-project-card-footer">
                  <small>{projectDateLabel(project.created_at)}</small>
                  <small>{pendingInvite ? `${project.member_role ?? "member"} invite pending` : projectActivityLabel(project)}</small>
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="v5-project-browser-state"><Icons.Project size={24} /><strong>No projects yet</strong><p>Create or import a Flow360-backed Thread project to begin.</p></div>
      )}
    </main>
  );
}

type WorkbenchLayout = typeof DEFAULT_WORKBENCH_LAYOUT;
type WorkbenchResizeAxis = "columns" | "rows";

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function readWorkbenchLayout(): WorkbenchLayout {
  if (typeof window === "undefined") return DEFAULT_WORKBENCH_LAYOUT;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(WORKBENCH_LAYOUT_STORAGE_KEY) ?? "{}");
    return {
      rightWidth: clampNumber(Number(parsed.rightWidth) || DEFAULT_WORKBENCH_LAYOUT.rightWidth, RIGHT_WIDTH_BOUNDS.min, RIGHT_WIDTH_BOUNDS.max),
      bottomHeight: clampNumber(Number(parsed.bottomHeight) || DEFAULT_WORKBENCH_LAYOUT.bottomHeight, BOTTOM_HEIGHT_BOUNDS.min, BOTTOM_HEIGHT_BOUNDS.max),
    };
  } catch {
    return DEFAULT_WORKBENCH_LAYOUT;
  }
}

function writeWorkbenchLayout(layout: WorkbenchLayout): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(WORKBENCH_LAYOUT_STORAGE_KEY, JSON.stringify(layout));
}

function workbenchStyle(layout: WorkbenchLayout): CSSProperties {
  return {
    "--v5-workbench-right-width": `${layout.rightWidth}px`,
    "--v5-workbench-bottom-height": `${layout.bottomHeight}px`,
  } as CSSProperties;
}

function cloneLayout(layout: ThreadV4LayoutJson): ThreadV4LayoutJson {
  return {
    ...layout,
    widgets: layout.widgets.map((widget) => ({
      ...widget,
      position: { ...widget.position },
      bindings: widget.bindings ? { ...widget.bindings } : undefined,
      state: widget.state ? { ...widget.state } : undefined,
    })),
  };
}

function dashboardLayoutFromWorkbench(layout: WorkbenchLayout): ThreadV4LayoutJson {
  const rightCols = layout.rightWidth >= 430 ? 4 : layout.rightWidth <= 280 ? 2 : 3;
  const bottomRows = layout.bottomHeight >= 240 ? 3 : layout.bottomHeight <= 140 ? 1 : 2;
  const centerCols = 12 - rightCols;
  return {
    schema_version: "thread.view.layout.v1",
    mode: "dashboard",
    cols: 12,
    row_height: 96,
    widgets: [
      { id: "runs", kind: "table", title: "Simulation runs", position: { x: 0, y: 0, w: centerCols, h: 5 } },
      { id: "artifacts", kind: "asset-rail", title: "Artifacts", position: { x: centerCols, y: 0, w: rightCols, h: 5 } },
      { id: "dag", kind: "dag", title: "DAG minimap", position: { x: 0, y: 5, w: 3, h: bottomRows } },
      { id: "notebook", kind: "notebook", title: "Spool", position: { x: 3, y: 5, w: 3, h: bottomRows } },
      { id: "geometry", kind: "geometry", title: "Geometry", position: { x: 6, y: 5, w: 3, h: bottomRows } },
      { id: "flowfield", kind: "flowfield", title: "Flowfield", position: { x: 9, y: 5, w: 3, h: bottomRows } },
    ],
  };
}

function defaultDashboardLayout(): ThreadV4LayoutJson {
  return {
    schema_version: "thread.view.layout.v1",
    mode: "dashboard",
    cols: 12,
    row_height: 96,
    widgets: [
      { id: "runs", kind: "table", title: "Simulation runs", position: { x: 0, y: 0, w: 7, h: 5 } },
      { id: "artifacts", kind: "asset-rail", title: "Artifacts", position: { x: 7, y: 0, w: 3, h: 5 } },
      { id: "flowfield", kind: "flowfield", title: "Flowfield", position: { x: 10, y: 0, w: 2, h: 5 } },
      { id: "dag", kind: "dag", title: "DAG minimap", position: { x: 0, y: 5, w: 3, h: 2 } },
      { id: "geometry", kind: "geometry", title: "Geometry", position: { x: 3, y: 5, w: 3, h: 2 } },
      { id: "notebook", kind: "notebook", title: "Spool", position: { x: 6, y: 5, w: 3, h: 2 } },
      { id: "plot", kind: "plot", title: "Plot", position: { x: 9, y: 5, w: 3, h: 2 } },
    ],
  };
}

const DASHBOARD_WIDGETS: Array<{ kind: ThreadV4WidgetKind; title: string; w: number; h: number; icon: ReactNode }> = [
  { kind: "table", title: "Data table", w: 6, h: 4, icon: <Icons.Table size={13} /> },
  { kind: "asset-rail", title: "Artifact shelf", w: 3, h: 4, icon: <Icons.Asset size={13} /> },
  { kind: "dag", title: "DAG minimap", w: 3, h: 2, icon: <Icons.Dag size={13} /> },
  { kind: "geometry", title: "Geometry", w: 3, h: 2, icon: <Icons.Cube size={13} /> },
  { kind: "flowfield", title: "Flowfield", w: 3, h: 2, icon: <Icons.Flowfield size={13} /> },
  { kind: "plot", title: "Plot", w: 4, h: 3, icon: <Icons.Plot size={13} /> },
  { kind: "notebook", title: "Spool notebook", w: 3, h: 2, icon: <Icons.MdNote size={13} /> },
  { kind: "markdown-note", title: "Notes card", w: 3, h: 2, icon: <Icons.MdNote size={13} /> },
];

function widgetIcon(kind: ThreadV4WidgetKind): ReactNode {
  return DASHBOARD_WIDGETS.find((widget) => widget.kind === kind)?.icon ?? <Icons.Project size={13} />;
}

function positionsOverlap(left: ThreadV4WidgetPosition, right: ThreadV4WidgetPosition): boolean {
  return left.x < right.x + right.w
    && left.x + left.w > right.x
    && left.y < right.y + right.h
    && left.y + left.h > right.y;
}

function positionsEqual(left: ThreadV4WidgetPosition, right: ThreadV4WidgetPosition): boolean {
  return left.x === right.x
    && left.y === right.y
    && left.w === right.w
    && left.h === right.h
    && (left.dock ?? "canvas") === (right.dock ?? "canvas");
}

function positionChangeMode(before: ThreadV4WidgetPosition, after: ThreadV4WidgetPosition): "move" | "resize" {
  return before.w === after.w && before.h === after.h ? "move" : "resize";
}

function widgetSpecSignature(widget: ThreadV4WidgetSpec): string {
  return JSON.stringify({
    id: widget.id,
    kind: widget.kind,
    title: widget.title,
    bindings: widget.bindings ?? null,
    state: widget.state ?? null,
  });
}

function canPlaceWidget(widgets: ThreadV4WidgetSpec[], widgetId: string, position: ThreadV4WidgetPosition, cols: number): boolean {
  if (position.x < 0 || position.y < 0 || position.w < DASHBOARD_MIN_WIDGET.w || position.h < DASHBOARD_MIN_WIDGET.h) return false;
  if (position.x + position.w > cols) return false;
  return widgets.every((widget) => widget.id === widgetId || !positionsOverlap(widget.position, position));
}

function placeWidget(layout: ThreadV4LayoutJson, widgetId: string, position: ThreadV4WidgetPosition): ThreadV4LayoutJson {
  const cols = layout.cols || 12;
  if (!canPlaceWidget(layout.widgets, widgetId, position, cols)) return layout;
  return {
    ...layout,
    widgets: layout.widgets.map((widget) => widget.id === widgetId ? { ...widget, position: { ...position } } : widget),
  };
}

function findOpenDashboardPosition(layout: ThreadV4LayoutJson, w: number, h: number): ThreadV4WidgetPosition {
  const cols = layout.cols || 12;
  const width = Math.min(w, cols);
  for (let y = 0; y < 40; y += 1) {
    for (let x = 0; x <= cols - width; x += 1) {
      const position = { x, y, w: width, h, dock: "canvas" as const };
      if (canPlaceWidget(layout.widgets, "__new__", position, cols)) return position;
    }
  }
  return { x: 0, y: Math.max(0, ...layout.widgets.map((widget) => widget.position.y + widget.position.h)), w: width, h, dock: "canvas" };
}

function addDashboardWidget(layout: ThreadV4LayoutJson, kind: ThreadV4WidgetKind): ThreadV4LayoutJson {
  const template = DASHBOARD_WIDGETS.find((widget) => widget.kind === kind) ?? DASHBOARD_WIDGETS[0]!;
  const id = `${kind}-${Date.now().toString(36)}`;
  const position = findOpenDashboardPosition(layout, template.w, template.h);
  return {
    ...layout,
    widgets: [
      ...layout.widgets,
      {
        id,
        kind,
        title: template.title,
        position,
      },
    ],
  };
}

function dashboardGridStyle(layout: ThreadV4LayoutJson): CSSProperties {
  const maxRows = Math.max(7, ...layout.widgets.map((widget) => widget.position.y + widget.position.h));
  return {
    "--v5-dashboard-cols": String(layout.cols || 12),
    "--v5-dashboard-row-height": `${layout.row_height || 96}px`,
    "--v5-dashboard-rows": String(maxRows),
  } as CSSProperties;
}

function WorkbenchResizeBar({ axis, onPointerDown, onReset }: { axis: WorkbenchResizeAxis; onPointerDown: (event: ReactPointerEvent<HTMLButtonElement>, axis: WorkbenchResizeAxis) => void; onReset: (axis: WorkbenchResizeAxis) => void }) {
  const horizontal = axis === "rows";
  return (
    <button
      aria-label={horizontal ? "Resize table and lower cards" : "Resize table and artifact shelf"}
      className={`v5-resize-bar is-${horizontal ? "horizontal" : "vertical"}`}
      title={horizontal ? "Drag to resize the table and lower cards" : "Drag to resize the table and artifact shelf"}
      type="button"
      onDoubleClick={() => onReset(axis)}
      onPointerDown={(event) => onPointerDown(event, axis)}
    />
  );
}

function hasRowValue(row: Record<string, unknown>, keys: readonly string[]): boolean {
  return keys.some((key) => {
    const value = row[key];
    if (typeof value === "string") return value.trim().length > 0;
    return value != null;
  });
}

function recordValue(row: Record<string, unknown>, key: string): unknown {
  return row[key];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function firstStringInRecords(records: readonly Record<string, unknown>[], keys: readonly string[]): string | null {
  for (const record of records) {
    for (const key of keys) {
      const value = recordValue(record, key);
      if (typeof value === "string" && value.trim()) return value.trim();
      if (typeof value === "number" && Number.isFinite(value)) return String(value);
    }
  }
  return null;
}

const FLOW360_HINT_SPECS = [
  { keys: ["flow360_case_id", "case_id"], targets: ["flow360_case_id", "case_id"] },
  { keys: ["uvf_asset_id"], targets: ["uvf_asset_id"] },
  { keys: ["flowfield_asset_id"], targets: ["flowfield_asset_id"] },
  { keys: ["visualization_status", "case_visualization_status"], targets: ["visualization_status"] },
  { keys: ["flow360_geometry_id", "geom_flow360_geometry_id", "geometry_id"], targets: ["geom_flow360_geometry_id", "flow360_geometry_id"] },
  { keys: ["flow360_surface_mesh_id", "surf_mesh_flow360_surface_mesh_id", "surface_mesh_id", "surf_mesh_id"], targets: ["surf_mesh_flow360_surface_mesh_id", "flow360_surface_mesh_id"] },
  { keys: ["flow360_volume_mesh_id", "vol_mesh_flow360_volume_mesh_id", "volume_mesh_id", "vol_mesh_id"], targets: ["vol_mesh_flow360_volume_mesh_id", "flow360_volume_mesh_id"] },
  { keys: ["name", "case_name", "flow360_case_name", "label"], targets: ["flow360_case_name"] },
] as const;

const FLOW360_CONDITION_ALIASES = [
  ["alpha", "alpha_deg"],
  ["beta", "beta_deg"],
  ["mach", "mach_number"],
  ["rpm", "RPM", "propeller_rpm"],
  ["velocity_magnitude"],
  ["re", "reynolds", "reynolds_number"],
  ["theta_ht"],
  ["t_mult", "T_mult", "thrust_mult"],
] as const;

function flow360HintsFromRecords(records: readonly Record<string, unknown>[]): Record<string, unknown> {
  const hints: Record<string, unknown> = {};
  for (const spec of FLOW360_HINT_SPECS) {
    const value = firstStringInRecords(records, spec.keys);
    if (!value) continue;
    for (const target of spec.targets) {
      if (!hints[target]) hints[target] = value;
    }
  }
  if ((hints.flow360_case_id || hints.case_id) && !hints.visualization_status) {
    hints.visualization_status = "completed";
  }
  return hints;
}

function nodeRecords(node: V3WorkbenchData["provenance"]["nodes"][number]): Record<string, unknown>[] {
  const base = { id: node.id, label: node.label, type: node.type, cluster: node.cluster };
  const attrs = node.attrs ?? {};
  const records: Record<string, unknown>[] = [base, attrs];
  for (const key of ["payloadPreview", "metadataPreview", "configPreview", "payload_preview", "metadata_preview", "config_preview"]) {
    const nested = attrs[key];
    if (isRecord(nested)) records.push(nested);
  }
  return records;
}

function flow360HintsFromNode(node: V3WorkbenchData["provenance"]["nodes"][number]): Record<string, unknown> {
  return flow360HintsFromRecords(nodeRecords(node));
}

function rowLineageIds(row: Record<string, unknown>): Set<string> {
  const ids = new Set<string>();
  const cellLineage = row._cell_lineage;
  if (isRecord(cellLineage)) {
    for (const lineage of Object.values(cellLineage)) {
      if (!isRecord(lineage)) continue;
      for (const key of ["artifact_id", "node_id", "source_node_id"]) {
        const value = lineage[key];
        if (typeof value === "string" && value.trim()) ids.add(value.trim());
      }
    }
  }
  const lineage = row._lineage ?? row.lineage;
  if (isRecord(lineage)) {
    const sources = lineage.source_rows;
    if (Array.isArray(sources)) {
      for (const source of sources) {
        if (!isRecord(source)) continue;
        for (const key of ["artifact_id", "node_id", "source_node_id", "row_id"]) {
          const value = source[key];
          if (typeof value === "string" && value.trim()) ids.add(value.trim());
        }
      }
    }
  }
  return ids;
}

function rowLineageLabels(row: Record<string, unknown>): string[] {
  const labels: string[] = [];
  const lineage = row._lineage ?? row.lineage;
  if (!isRecord(lineage) || !Array.isArray(lineage.source_rows)) return labels;
  for (const source of lineage.source_rows) {
    if (!isRecord(source)) continue;
    const label = source.artifact_label;
    if (typeof label === "string" && label.trim()) labels.push(label.trim().toLowerCase());
  }
  return labels;
}

function numberFromAliases(record: Record<string, unknown>, keys: readonly string[]): number | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

function numbersClose(left: number, right: number): boolean {
  const scale = Math.max(1, Math.abs(left), Math.abs(right));
  return Math.abs(left - right) <= scale * 1e-6;
}

function nodeMatchesRowConditions(row: Record<string, unknown>, records: readonly Record<string, unknown>[]): boolean {
  let matched = 0;
  for (const aliases of FLOW360_CONDITION_ALIASES) {
    const rowValue = numberFromAliases(row, aliases);
    if (rowValue == null) continue;
    const candidateValue = records.map((record) => numberFromAliases(record, aliases)).find((value): value is number => value != null);
    if (candidateValue == null) return false;
    if (!numbersClose(rowValue, candidateValue)) return false;
    matched += 1;
  }
  return matched >= 2;
}

function nodeMatchesRowLineage(node: V3WorkbenchData["provenance"]["nodes"][number], row: Record<string, unknown>): boolean {
  const lineageIds = rowLineageIds(row);
  if (lineageIds.has(node.id)) return true;
  const labels = rowLineageLabels(row);
  if (!labels.length) return false;
  const nodeLabel = node.label.toLowerCase();
  return labels.some((label) => label === nodeLabel || nodeLabel.includes(label) || label.includes(nodeLabel));
}

function flow360HintsForSelectedRow(data: V3WorkbenchData, row: Record<string, unknown> | null): Record<string, unknown> {
  if (!row) return {};

  for (const node of data.provenance.nodes) {
    if (!nodeMatchesRowLineage(node, row)) continue;
    const hints = flow360HintsFromNode(node);
    if (Object.keys(hints).length) return hints;
  }

  const conditionMatches: Record<string, unknown>[] = [];
  for (const node of data.provenance.nodes) {
    const records = nodeRecords(node);
    const hints = flow360HintsFromRecords(records);
    if (!Object.keys(hints).length) continue;
    if (nodeMatchesRowConditions(row, records)) conditionMatches.push(hints);
  }

  return conditionMatches.length === 1 ? conditionMatches[0] ?? {} : {};
}

function mergeFlow360Hints(row: Record<string, unknown>, hints: Record<string, unknown>): Record<string, unknown> {
  const hadCase = hasRowValue(row, ["flow360_case_id", "case_id"]);
  const merged = { ...row };
  for (const [key, value] of Object.entries(hints)) {
    if (!hasRowValue(merged, [key])) merged[key] = value;
  }
  if (!hadCase && hasRowValue(hints, ["flow360_case_id", "case_id"])) {
    merged.visualization_status = hints.visualization_status ?? "completed";
  }
  return merged;
}

const FLOW360_CASE_ID_RE = /^case-[a-z0-9-]+$/i;

function selectedFlow360CaseId(...ids: Array<string | null | undefined>): string | null {
  for (const id of ids) {
    const value = typeof id === "string" ? id.trim() : "";
    if (value && FLOW360_CASE_ID_RE.test(value)) return value;
  }
  return null;
}

function rowString(row: Record<string, unknown>, key: string): string | null {
  const value = row[key];
  if (typeof value === "string" && value.trim()) return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
}

function rowMatchesSelection(row: Record<string, unknown>, selectedRunId: string | null): boolean {
  if (!selectedRunId) return false;
  return rowKey(row as ThreadRow) === selectedRunId || rowString(row, "runId") === selectedRunId || rowString(row, "name") === selectedRunId;
}

function rowMatchesFlow360Case(row: Record<string, unknown>, caseId: string): boolean {
  return rowString(row, "flow360_case_id") === caseId || rowString(row, "case_id") === caseId;
}

function rowMatchesNodeSelection(row: Record<string, unknown>, selectedNodeId: string | null): boolean {
  if (!selectedNodeId) return false;
  return ["_node_id", "node_id", "rowNodeId", "source_node_id"].some((key) => rowString(row, key) === selectedNodeId);
}

function rowMatchesLineageSelection(row: Record<string, unknown>, selectedNodeId: string | null): boolean {
  return Boolean(selectedNodeId && rowLineageIds(row).has(selectedNodeId));
}

function singleMatchingRow(rows: readonly Record<string, unknown>[], matches: (row: Record<string, unknown>) => boolean): Record<string, unknown> | null {
  const matchingRows = rows.filter(matches);
  return matchingRows.length === 1 ? matchingRows[0] ?? null : null;
}

function rowForPreviewSelection(rows: readonly Record<string, unknown>[], selectedRunId: string | null, selectedNodeId: string | null): Record<string, unknown> | null {
  return rows.find((row) => rowMatchesSelection(row, selectedRunId))
    ?? singleMatchingRow(rows, (row) => rowMatchesLineageSelection(row, selectedNodeId))
    ?? singleMatchingRow(rows, (row) => rowMatchesNodeSelection(row, selectedNodeId));
}

function rowsHavePreviewValue(rows: readonly Record<string, unknown>[], keys: readonly string[], selectedRunId: string | null, selectedNodeId: string | null): boolean {
  const selectedRow = rowForPreviewSelection(rows, selectedRunId, selectedNodeId);
  if (selectedRunId || selectedNodeId) return Boolean(selectedRow && hasRowValue(selectedRow, keys));
  return rows.some((row) => hasRowValue(row, keys));
}

function flow360RowsForSelection(data: V3WorkbenchData, selectedRunId: string | null, selectedNodeId: string | null, focusedAssetId?: string | null): Record<string, unknown>[] {
  const rows = data.rows as Record<string, unknown>[];
  const caseId = selectedFlow360CaseId(selectedNodeId, focusedAssetId, selectedRunId);
  const selectedRow = rowForPreviewSelection(rows, selectedRunId, selectedNodeId);
  const hints = {
    ...flow360HintsForSelectedRow(data, selectedRow ?? null),
    ...(caseId ? {
      flow360_case_id: caseId,
      case_id: caseId,
      visualization_status: "completed",
    } : {}),
  };

  if (!Object.keys(hints).length) return rows;
  if (caseId && !selectedRow && rows.some((row) => rowMatchesFlow360Case(row, caseId))) return rows;

  const fallbackRow = mergeFlow360Hints({
    ...(selectedRow ?? {}),
    name: selectedRunId ?? rowString(selectedRow ?? {}, "name") ?? caseId,
    runId: selectedRunId ?? rowString(selectedRow ?? {}, "runId") ?? caseId,
    _node_id: selectedNodeId ?? caseId ?? rowString(selectedRow ?? {}, "_node_id"),
  }, hints);

  if (!selectedRow) return [fallbackRow, ...rows];
  return rows.map((row) => (row === selectedRow ? fallbackRow : row));
}

function flowfieldRowsForSelection(data: V3WorkbenchData, selectedRunId: string | null, selectedNodeId: string | null, focusedAssetId?: string | null): Record<string, unknown>[] {
  return flow360RowsForSelection(data, selectedRunId, selectedNodeId, focusedAssetId);
}

function hasFlowfieldAsset(assets: readonly ThreadAsset[]): boolean {
  return assets.some((asset) => asset.type === "uvf" || asset.preview?.kind === "uvf");
}

const FLOW360_GEOMETRY_KEYS = [
  "geom_flow360_geometry_id",
  "flow360_geometry_id",
  "geometry_id",
  "surf_mesh_flow360_surface_mesh_id",
  "flow360_surface_mesh_id",
  "surface_mesh_id",
  "surf_mesh_id",
  "vol_mesh_flow360_volume_mesh_id",
  "flow360_volume_mesh_id",
  "volume_mesh_id",
  "vol_mesh_id",
];

function hasFlow360GeometryData(data: V3WorkbenchData): boolean {
  if (data.rows.some((row) => hasRowValue(row as Record<string, unknown>, FLOW360_GEOMETRY_KEYS))) return true;
  return data.provenance.nodes.some((node) => {
    const hints = flow360HintsFromNode(node);
    return hasRowValue(node.attrs ?? {}, FLOW360_GEOMETRY_KEYS) || hasRowValue(hints, FLOW360_GEOMETRY_KEYS);
  });
}

function hasGeometryData(data: V3WorkbenchData): boolean {
  if (data.provenance.nodes.some((node) => node.type === "geometry" || node.type === "mesh")) return true;
  if (data.assets.some(isGeometryPreviewAsset)) return true;
  return hasFlow360GeometryData(data);
}

export function WorkbenchSurface(props: SurfaceProps) {
  const { data, dataLoading, projectId, projectName, branch, persistedProject, selectedRunId, selectedNodeId, focusedAssetId, tableMode, notebookEntries, onTableModeChange, onSelectRun, onOpenArtifact, onOpenFlow360, onOpenFocus, onOpenView, onSaveDashboard } = props;
  const workbenchRef = useRef<HTMLElement | null>(null);
  const [layout, setLayout] = useState<WorkbenchLayout>(() => readWorkbenchLayout());
  const [savePromptOpen, setSavePromptOpen] = useState(false);
  const [savePromptName, setSavePromptName] = useState("My V5 view");
  const rows = data.totalRowCount || data.rows.length;
  const empty = !dataLoading && rows === 0;
  const emptyReason = data.errors[0] ?? null;
  const hasGeometry = hasGeometryData(data);

  useEffect(() => writeWorkbenchLayout(layout), [layout]);

  const resetResize = useCallback((axis: WorkbenchResizeAxis) => {
    setLayout((current) => axis === "columns"
      ? { ...current, rightWidth: DEFAULT_WORKBENCH_LAYOUT.rightWidth }
      : { ...current, bottomHeight: DEFAULT_WORKBENCH_LAYOUT.bottomHeight });
  }, []);

  const beginResize = useCallback((event: ReactPointerEvent<HTMLButtonElement>, axis: WorkbenchResizeAxis) => {
    event.preventDefault();
    const rect = workbenchRef.current?.getBoundingClientRect();
    const usableWidth = rect?.width && rect.width > 0 ? rect.width : window.innerWidth;
    const usableHeight = rect?.height && rect.height > 0 ? rect.height : window.innerHeight;
    const maxRightWidth = clampNumber(usableWidth * 0.46, RIGHT_WIDTH_BOUNDS.min, RIGHT_WIDTH_BOUNDS.max);
    const maxBottomHeight = clampNumber(usableHeight - 430, BOTTOM_HEIGHT_BOUNDS.min, BOTTOM_HEIGHT_BOUNDS.max);
    const startX = event.clientX;
    const startY = event.clientY;
    const startLayout = layout;
    let moved = false;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;

    document.body.style.cursor = axis === "columns" ? "col-resize" : "row-resize";
    document.body.style.userSelect = "none";

    const handleMove = (moveEvent: PointerEvent) => {
      moved = true;
      setLayout(() => {
        if (axis === "columns") {
          return {
            ...startLayout,
            rightWidth: clampNumber(startLayout.rightWidth + startX - moveEvent.clientX, RIGHT_WIDTH_BOUNDS.min, maxRightWidth),
          };
        }
        return {
          ...startLayout,
          bottomHeight: clampNumber(startLayout.bottomHeight + startY - moveEvent.clientY, BOTTOM_HEIGHT_BOUNDS.min, maxBottomHeight),
        };
      });
    };

    const handleUp = () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
      if (moved) setSavePromptOpen(true);
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
  }, [layout]);

  return (
    <main className="v5-main v5-workbench" data-testid="v5-workbench" ref={workbenchRef} style={workbenchStyle(layout)}>
      {savePromptOpen ? (
        <section className="v5-save-view-prompt" aria-label="Save modified default view">
          <div>
            <strong>Do you want to save this as a new view?</strong>
            <span>The default workbench layout changed. Save it to the Thread DB as a custom dashboard.</span>
          </div>
          <input
            aria-label="New view name"
            value={savePromptName}
            onChange={(event) => setSavePromptName(event.currentTarget.value)}
          />
          <button
            className="v5-primary-button"
            type="button"
            onClick={() => {
              onSaveDashboard({
                name: savePromptName.trim() || "My V5 view",
                layout_json: dashboardLayoutFromWorkbench(layout),
                binding_json: selectedRunId ? { selected_run_id: selectedRunId } : {},
                metadata: { source: "thread-v5-default-workbench", workbench_layout: layout },
              });
              setSavePromptOpen(false);
            }}
          >
            <Icons.Save size={14} />Save as new view
          </button>
          <button className="v5-text-button" type="button" onClick={() => setSavePromptOpen(false)}>Later</button>
        </section>
      ) : null}
      <section className="v5-center">
        <Panel title="Simulation runs" eyebrow={tableMode === "table" ? "table" : "correlogram"} actions={<TableToggle tableMode={tableMode} onChange={onTableModeChange} />}>
          {dataLoading ? <WorkbenchLoadingPanel projectName={projectName} /> : empty ? <EmptyProjectState projectName={projectName} persistedProject={persistedProject} hasGeometry={hasGeometry} reason={emptyReason} onOpenFlow360={onOpenFlow360} /> : <RunsTable data={data} selectedRunId={selectedRunId} onSelectRun={onSelectRun} mode={tableMode} />}
        </Panel>
        <WorkbenchResizeBar axis="rows" onPointerDown={beginResize} onReset={resetResize} />
        <div className="v5-bottom-strip">
          {dataLoading ? <LoadingStripCard title="DAG minimap" meta="loading graph" icon={<Icons.Dag size={13} />} /> : <ThreadV5DagMinimap data={data} selectedRunId={selectedRunId} noteCount={notebookEntries.length} onExpand={() => onOpenFocus("dag")} />}
          {dataLoading ? <LoadingStripCard title="Spool" meta="loading notes" icon={<Icons.MdNote size={15} />} /> : <NotebookStrip selectedRunId={selectedRunId} noteCount={notebookEntries.length} onOpen={() => onOpenFocus("notebook")} />}
          <ConditionPivotStripCard loading={dataLoading} rowCount={data.totalRowCount || data.rows.length} onOpen={() => onOpenView("data")} />
          <GeometryPreviewCard data={data} loading={dataLoading} projectId={projectId} branch={branch} persistedProject={persistedProject} selectedRunId={selectedRunId} selectedNodeId={selectedNodeId} available={hasGeometry} onOpen={() => onOpenFocus("geometry")} />
          <FlowfieldPreviewCard data={data} loading={dataLoading} projectId={projectId} branch={branch} persistedProject={persistedProject} selectedRunId={selectedRunId} selectedNodeId={selectedNodeId} onOpen={() => onOpenFocus("flowfield")} />
        </div>
      </section>
      <WorkbenchResizeBar axis="columns" onPointerDown={beginResize} onReset={resetResize} />
      <aside className="v5-right">
        <ArtifactShelf assets={data.assets} loading={dataLoading} selectedRunId={selectedRunId} focusedAssetId={focusedAssetId} onOpenArtifact={onOpenArtifact} />
      </aside>
    </main>
  );
}

export function HomeSurface(props: SurfaceProps) {
  const { data, projectName, branch, views, notebookEntries, onOpenView, onOpenDashboard, onSelectRun, onOpenArtifact } = props;
  const rows = data.totalRowCount || data.rows.length;
  const complete = data.rows.filter((row) => String(row.status ?? row.state ?? "").toLowerCase().includes("complete") || rowState(row) === "fresh").length;
  return (
    <main className="v5-main v5-home">
      <section className="v5-home-hero">
        <span className="v5-eyebrow">Project - live</span>
        <h1>{projectName}</h1>
        <p>{branch} - {rows ? `${rows} runs` : "project connected"} - {data.assets.length} artifacts - {notebookEntries.length} notes</p>
        <div className="v5-hero-actions">
          <button className="v5-primary-button" type="button" onClick={() => onOpenView("workbench")}>Open workbench <Icons.ChevronRight size={14} /></button>
          <button className="v5-text-button" type="button" onClick={() => onOpenView("dashboard-edit")}><Icons.Plus size={13} />New board</button>
        </div>
      </section>
      <section className="v5-stat-row">
        <Stat label="Runs" value={rows} />
        <Stat label="Complete" value={complete} />
        <Stat label="Artifacts" value={data.assets.length} />
        <Stat label="Notes" value={notebookEntries.length} />
        <Stat label="DAG nodes" value={data.provenance.representedScale.totalNodes} />
      </section>
      <div className="v5-home-grid">
        <Panel title="Recent runs" eyebrow="activity">
          <RunList rows={data.rows.slice(0, 8)} selectedRunId={null} onSelectRun={onSelectRun} />
        </Panel>
        <Panel title="New artifacts" eyebrow={`${data.assets.length} assets`}>
          <ArtifactList assets={data.assets.slice(0, 8)} selectedRunId={null} onOpenArtifact={onOpenArtifact} />
        </Panel>
        <Panel title="Boards" eyebrow="saved views">
          <div className="v5-board-list">
            {views.length ? views.slice(0, 4).map((view) => <button key={view.id} type="button" onClick={() => onOpenDashboard(view)}><Icons.SavedPlots size={15} /><span>{view.name}</span><em>{view.layout_json.widgets.length} widgets</em></button>) : <p className="v5-muted">Saved dashboards will appear here.</p>}
          </div>
        </Panel>
      </div>
    </main>
  );
}

export function DataSurface(props: SurfaceProps) {
  const { data, projectId, branch, selectedRunId, tableMode, onTableModeChange, onSelectRun, onOpenArtifact, onOpenView } = props;
  const [dataTab, setDataTab] = useState<"runs" | "pivot">("runs");
  const btnBase: React.CSSProperties = { padding: "2px 8px", borderRadius: 4, border: "1px solid var(--gray-a6)", fontSize: 12, cursor: "pointer" };
  const tabBtn = (active: boolean): React.CSSProperties => ({
    ...btnBase, background: active ? "var(--accent-9)" : "var(--gray-a3)", color: active ? "white" : "var(--gray-12)",
  });
  return (
    <main className="v5-main">
      <Panel
        title="Data"
        eyebrow={dataTab === "pivot" ? "condition pivot" : tableMode}
        actions={
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <button type="button" data-testid="data-tab-runs" style={tabBtn(dataTab === "runs")} onClick={() => setDataTab("runs")}>
              Runs
            </button>
            <button type="button" data-testid="data-tab-pivot" style={tabBtn(dataTab === "pivot")} onClick={() => setDataTab("pivot")}>
              Condition pivot
            </button>
            {dataTab === "runs" && <TableToggle tableMode={tableMode} onChange={onTableModeChange} />}
          </div>
        }
      >
        {dataTab === "runs" ? (
          <RunsTable data={data} selectedRunId={selectedRunId} onSelectRun={onSelectRun} mode={tableMode} />
        ) : (
          <ConditionPivotTable projectId={projectId} branch={branch} isVisible />
        )}
      </Panel>
      <div className="v5-data-jump-row">
        <button type="button" onClick={() => onOpenView("artifacts")}><Icons.Asset size={14} />Open artifacts</button>
        <button type="button" onClick={() => onOpenView("provenance")}><Icons.Dag size={14} />Open provenance</button>
        {data.assets[0] ? <button type="button" onClick={() => onOpenArtifact(data.assets[0]!.id)}><Icons.Maximize size={14} />Focus latest artifact</button> : null}
      </div>
    </main>
  );
}

export function ArtifactsSurface({ data, selectedRunId, focusedAssetId, onOpenArtifact }: SurfaceProps) {
  const grouped = groupAssets(data.assets);
  return (
    <main className="v5-main v5-artifacts-page">
      <header className="v5-page-header">
        <div><span className="v5-eyebrow">Artifacts</span><h1>Artifact decks</h1></div>
        <span className="v5-chip">{data.assets.length} assets</span>
      </header>
      {data.assets.length ? (
        <div className="v5-deck-grid">
          {grouped.map(([kind, assets]) => <ArtifactDeck key={kind} kind={kind} assets={assets} selectedRunId={selectedRunId} focusedAssetId={focusedAssetId} onOpen={onOpenArtifact} />)}
        </div>
      ) : (
        <EmptyShelf />
      )}
    </main>
  );
}

export function ProvenanceSurface({ data, projectId, persistedProject, selectedRunId, selectedNodeId, notebookEntries, onSelectRun }: SurfaceProps) {
  return (
    <main className="v5-main">
      <ThreadV5BundleProvenance data={data} selectedRunId={selectedRunId} noteCount={notebookEntries.length} full onSelectRun={onSelectRun} />
      {persistedProject && (
        // FXC-8618: inline collapse-by-default lineage explorer below the bundle overview.
        <section className="v5-provenance-lineage" aria-label="Lineage node-link DAG">
          <header className="v5-panel-header">
            <div>
              <span className="v5-eyebrow">Node-link graph</span>
              <h2>Lineage DAG</h2>
            </div>
          </header>
          <div className="v5-provenance-lineage-canvas">
            <ThreadV5LineageExplorer
              projectId={projectId}
              branch={data.branch || "main"}
              selectedNodeId={selectedNodeId}
              onNodeSelect={(nodeId) => onSelectRun(nodeId)}
            />
          </div>
        </section>
      )}
    </main>
  );
}

// FXC-8628 / FXC-8630: Full-surface lineage view.
//
// Renders ThreadV5LineageExplorer (from FXC-8618) as a full-page surface — the
// same component reused and extended, wired to the active project/branch from
// surfaceProps. This is the nav-reachable "/lineage" view; the existing inline
// mounts in ProvenanceSurface and FocusLayer remain unchanged.
export function LineageFullSurface({ projectId, branch, selectedNodeId, persistedProject, onSelectRun, onOpenView }: SurfaceProps) {
  if (!persistedProject) {
    return (
      <main className="v5-main v5-lineage-full" data-testid="lineage-full-surface">
        <header className="v5-page-header v5-lineage-full-header">
          <div>
            <span className="v5-eyebrow">Graph</span>
            <h1>Lineage</h1>
          </div>
        </header>
        <section className="v5-lineage-full-empty" role="status">
          <Icons.Dag size={28} />
          <strong>Live project required</strong>
          <p>Open a Thread project to explore its provenance lineage graph.</p>
          <button className="v5-primary-button" type="button" onClick={() => onOpenView("projects")}>
            Open projects
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="v5-main v5-lineage-full" data-testid="lineage-full-surface">
      <header className="v5-page-header v5-lineage-full-header">
        <div>
          <span className="v5-eyebrow">Graph</span>
          <h1>Lineage</h1>
          <p>Full provenance DAG for this project — click to expand, right-click to re-centre.</p>
        </div>
        <div className="v5-lineage-full-actions">
          <button
            className="v5-text-button"
            type="button"
            onClick={() => onOpenView("provenance")}
            title="Switch to the tabular provenance view"
          >
            <Icons.Dag size={13} />
            Provenance table
          </button>
        </div>
      </header>
      <section className="v5-lineage-full-canvas" aria-label="Lineage graph">
        {/* FXC-8628/8630: Reuse ThreadV5LineageExplorer (FXC-8618) as the full-surface view.
            The component is wired to the active project_id + branch from workbench context,
            NOT hardcoded "main". The highest-degree focusId guard and empty-graph guard are
            handled inside ThreadV5LineageExplorer. The search box (FXC-8632) and selection
            panel are also rendered by the shared component via its extended props. */}
        <ThreadV5LineageExplorer
          projectId={projectId}
          branch={branch || "main"}
          selectedNodeId={selectedNodeId}
          onNodeSelect={(nodeId) => onSelectRun(nodeId)}
          fullSurface
        />
      </section>
    </main>
  );
}

export function BranchingSurface({ branch, onOpenView }: SurfaceProps) {
  return (
    <main className="v5-main v5-branching">
      <section className="v5-branch-graph">
        <span className="v5-eyebrow">Branching</span>
        <h1>Project branches</h1>
        <div className="v5-branch-lanes">
          {["main", "jen-sideslip-sweep", "experimental-mesh-v13", "archive/clean-baseline"].map((name, index) => (
            <button key={name} className={name === branch ? "is-current" : ""} type="button" onClick={() => onOpenView("workbench")}>
              <Icons.Branch size={14} />
              <strong>{name}</strong>
              <span>{index === 0 ? "current" : index === 1 ? "ready to merge" : index === 2 ? "+1 / -7" : "archived"}</span>
            </button>
          ))}
        </div>
      </section>
    </main>
  );
}

export function ProjectMembersSurface({ projectId, projectName, persistedProject, onOpenView }: SurfaceProps) {
  return (
    <main className="v5-main v5-members-page">
      <header className="v5-page-header v5-members-header">
        <div>
          <span className="v5-eyebrow">Access</span>
          <h1>Project members</h1>
          <p>{projectName}</p>
        </div>
        <button className="v5-text-button" type="button" onClick={() => onOpenView("workbench")}><Icons.GridIcon size={13} />Workbench</button>
      </header>
      {persistedProject ? (
        <section className="v5-members-card" aria-label="Project members">
          <MembersPanel projectId={projectId} />
        </section>
      ) : (
        <section className="v5-members-state">
          <Icons.Project size={24} />
          <strong>Live project required</strong>
          <p>Member access is available once this workspace is backed by a Thread project.</p>
        </section>
      )}
    </main>
  );
}

function DashboardPalette({ disabled = false, onAdd }: { disabled?: boolean; onAdd: (kind: ThreadV4WidgetKind) => void }) {
  return (
    <aside className="v5-widget-palette" aria-label="Dashboard widgets">
      <span className="v5-eyebrow">Add panels</span>
      {DASHBOARD_WIDGETS.map((widget) => (
        <button key={widget.kind} type="button" onClick={() => onAdd(widget.kind)} disabled={disabled}>
          <Icons.Plus size={13} />{widget.title}
        </button>
      ))}
    </aside>
  );
}

function DashboardCanvas({
  layout,
  props,
  editable,
  lockedWidgets,
  onLayoutChange,
  onRemoveWidget,
  onWidgetEditing,
}: {
  layout: ThreadV4LayoutJson;
  props: SurfaceProps;
  editable: boolean;
  lockedWidgets?: Map<string, ThreadDashboardEditingEvent>;
  onLayoutChange: (layout: ThreadV4LayoutJson) => void;
  onRemoveWidget?: (widgetId: string) => void;
  onWidgetEditing?: (widgetId: string, mode: "move" | "resize") => void;
}) {
  const canvasRef = useRef<HTMLElement | null>(null);
  const { data, dataLoading, selectedRunId, focusedAssetId, selectedNodeId, projectId, branch, persistedProject, tableMode, notebookEntries, onSelectRun, onOpenArtifact, onOpenFocus } = props;
  const hasGeometry = hasGeometryData(data);

  const beginWidgetInteraction = useCallback((event: ReactPointerEvent<HTMLButtonElement>, widget: ThreadV4WidgetSpec, mode: "move" | "resize") => {
    if (!editable) return;
    event.preventDefault();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cols = layout.cols || 12;
    const rowHeight = layout.row_height || 96;
    const cellWidth = Math.max(24, (rect.width - DASHBOARD_GRID_GAP * (cols - 1)) / cols);
    const startX = event.clientX;
    const startY = event.clientY;
    const startPosition = { ...widget.position };
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;

    onWidgetEditing?.(widget.id, mode);
    document.body.style.cursor = mode === "move" ? "grabbing" : "nwse-resize";
    document.body.style.userSelect = "none";

    const handleMove = (moveEvent: PointerEvent) => {
      const dx = Math.round((moveEvent.clientX - startX) / cellWidth);
      const dy = Math.round((moveEvent.clientY - startY) / rowHeight);
      const nextPosition = mode === "move"
        ? {
            ...startPosition,
            x: clampNumber(startPosition.x + dx, 0, cols - startPosition.w),
            y: Math.max(0, startPosition.y + dy),
          }
        : {
            ...startPosition,
            w: clampNumber(startPosition.w + dx, DASHBOARD_MIN_WIDGET.w, cols - startPosition.x),
            h: Math.max(DASHBOARD_MIN_WIDGET.h, startPosition.h + dy),
          };
      onLayoutChange(placeWidget(layout, widget.id, nextPosition));
    };

    const handleUp = () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
  }, [editable, layout, onLayoutChange, onWidgetEditing]);

  return (
    <section ref={canvasRef} className={`v5-dashboard-canvas ${editable ? "is-editable" : ""}`} style={dashboardGridStyle(layout)} data-testid="v5-dashboard-canvas">
      {layout.widgets.map((widget) => {
        const lockedBy = lockedWidgets?.get(widget.id);
        const lockLabel = lockedBy ? (lockedBy.display_name || lockedBy.user_id) : null;
        const widgetEditable = editable && !lockedBy;
        return (
          <article
            key={widget.id}
            className={`v5-dashboard-widget is-${widget.kind} ${lockedBy ? "is-locked" : ""}`}
            style={{
              gridColumn: `${widget.position.x + 1} / span ${widget.position.w}`,
              gridRow: `${widget.position.y + 1} / span ${widget.position.h}`,
            }}
          >
            <header className="v5-dashboard-widget-header">
              <button
                className="v5-dashboard-move"
                type="button"
                aria-label={`Move ${widget.title}`}
                disabled={!widgetEditable}
                title={lockLabel ? `${lockLabel} is editing` : undefined}
                onPointerDown={(event) => beginWidgetInteraction(event, widget, "move")}
              >
                {widgetIcon(widget.kind)}<span>{widget.title}</span>
              </button>
              {lockLabel ? <span className="v5-dashboard-lock" title={`${lockLabel} is editing`}>{identityInitials(lockLabel)}</span> : null}
              {editable ? (
                <button className="v5-icon-button" type="button" aria-label={`Remove ${widget.title}`} onClick={() => onRemoveWidget?.(widget.id)} disabled={!widgetEditable}>
                  <Icons.Close size={12} />
                </button>
              ) : null}
            </header>
            <div className="v5-dashboard-widget-body">
              <DashboardWidgetContent
                widget={widget}
                data={data}
                dataLoading={dataLoading}
                selectedRunId={selectedRunId}
                selectedNodeId={selectedNodeId}
                focusedAssetId={focusedAssetId}
                tableMode={tableMode}
                projectId={projectId}
                branch={branch}
                persistedProject={persistedProject}
                notebookEntries={notebookEntries}
                hasGeometry={hasGeometry}
                onSelectRun={onSelectRun}
                onOpenArtifact={onOpenArtifact}
                onOpenFocus={onOpenFocus}
              />
            </div>
            {editable ? (
              <button
                className="v5-dashboard-resize"
                type="button"
                aria-label={`Resize ${widget.title}`}
                disabled={!widgetEditable}
                title={lockLabel ? `${lockLabel} is editing` : undefined}
                onPointerDown={(event) => beginWidgetInteraction(event, widget, "resize")}
              />
            ) : null}
          </article>
        );
      })}
    </section>
  );
}

function DashboardWidgetContent({
  widget,
  data,
  dataLoading,
  selectedRunId,
  selectedNodeId,
  focusedAssetId,
  tableMode,
  projectId,
  branch,
  persistedProject,
  notebookEntries,
  hasGeometry,
  onSelectRun,
  onOpenArtifact,
  onOpenFocus,
}: {
  widget: ThreadV4WidgetSpec;
  data: V3WorkbenchData;
  dataLoading: boolean;
  selectedRunId: string | null;
  selectedNodeId: string | null;
  focusedAssetId: string | null;
  tableMode: V5TableMode;
  projectId: string;
  branch: string;
  persistedProject: boolean;
  notebookEntries: ThreadV4NotebookEntry[];
  hasGeometry: boolean;
  onSelectRun: SelectRunHandler;
  onOpenArtifact: (assetId: string) => void;
  onOpenFocus: (focus: V5Focus) => void;
}) {
  if (dataLoading) return <CardLoadingPanel label={`Loading ${widget.title.toLowerCase()}`} />;
  switch (widget.kind) {
    case "table":
      return <RunsTable data={data} selectedRunId={selectedRunId} onSelectRun={onSelectRun} mode={tableMode} />;
    case "asset-rail":
      return data.assets.length ? <ArtifactList assets={data.assets} selectedRunId={selectedRunId} focusedAssetId={focusedAssetId} onOpenArtifact={onOpenArtifact} /> : <EmptyShelf />;
    case "dag":
      return <ThreadV5DagMinimap data={data} selectedRunId={selectedRunId} noteCount={notebookEntries.length} onExpand={() => onOpenFocus("dag")} />;
    case "geometry":
    case "mesh":
      return <GeometryPreviewCard data={data} loading={false} projectId={projectId} branch={branch} persistedProject={persistedProject} selectedRunId={selectedRunId} selectedNodeId={selectedNodeId} available={hasGeometry} onOpen={() => onOpenFocus("geometry")} />;
    case "flowfield":
      return <FlowfieldPreviewCard data={data} loading={false} projectId={projectId} branch={branch} persistedProject={persistedProject} selectedRunId={selectedRunId} selectedNodeId={selectedNodeId} onOpen={() => onOpenFocus("flowfield")} />;
    case "plot": {
      const plot = data.assets.find((asset) => asset.type === "plot" && assetLinkedToRun(asset, selectedRunId)) ?? data.assets.find((asset) => asset.type === "plot");
      return plot ? (
        <button className="v5-dashboard-plot-card" type="button" onClick={() => onOpenArtifact(plot.id)}>
          <ArtifactThumb asset={plot} large />
          <span>{plot.label}</span>
        </button>
      ) : <EmptyShelf />;
    }
    case "notebook":
    case "markdown-note":
      return <NotebookStrip selectedRunId={selectedRunId} noteCount={notebookEntries.length} onOpen={() => onOpenFocus("notebook")} />;
    case "build-surface":
    default:
      return (
        <div className="v5-dashboard-placeholder">
          {widgetIcon(widget.kind)}
          <strong>{widget.title}</strong>
          <span>Panel ready for binding</span>
        </div>
      );
  }
}

export function DashboardViewSurface(props: SurfaceProps) {
  const { currentView, persistedProject, selectedRunId, onSaveDashboard, onOpenView } = props;
  const [layout, setLayout] = useState<ThreadV4LayoutJson>(() => cloneLayout(currentView?.layout_json ?? defaultDashboardLayout()));
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setLayout(cloneLayout(currentView?.layout_json ?? defaultDashboardLayout()));
    setDirty(false);
  }, [currentView?.id, currentView?.layout_json]);

  const applySharedLayout = useCallback((next: ThreadV4LayoutJson) => {
    setLayout(cloneLayout(next));
    setDirty(false);
  }, []);
  const collaboration = useDashboardLayoutCollaboration({
    viewId: currentView?.id ?? null,
    enabled: Boolean(persistedProject && currentView?.id),
    onRemoteLayout: applySharedLayout,
    onLocalLayoutPersisted: applySharedLayout,
  });
  const lockedWidgets = useMemo(() => {
    const entries = collaboration.editingUsers
      .filter((event) => event.widget_id)
      .map((event) => [event.widget_id!, event] as const);
    return new Map(entries);
  }, [collaboration.editingUsers]);
  const canEditDashboard = collaboration.access !== "read";

  const publishLayoutDelta = useCallback((previous: ThreadV4LayoutJson, next: ThreadV4LayoutJson) => {
    const previousWidgets = new Map(previous.widgets.map((widget) => [widget.id, widget]));
    const nextWidgets = new Map(next.widgets.map((widget) => [widget.id, widget]));
    const removedWidget = previous.widgets.find((widget) => !nextWidgets.has(widget.id));
    if (removedWidget) {
      collaboration.removeWidget(removedWidget.id);
      return;
    }
    const addedWidget = next.widgets.find((widget) => !previousWidgets.has(widget.id));
    if (addedWidget) {
      collaboration.upsertWidget(addedWidget);
      return;
    }
    const specChangedWidget = next.widgets.find((widget) => {
      const before = previousWidgets.get(widget.id);
      if (!before) return false;
      return widgetSpecSignature(before) !== widgetSpecSignature(widget);
    });
    if (specChangedWidget) {
      collaboration.upsertWidget(specChangedWidget);
      return;
    }
    const positionChangedWidget = next.widgets.find((widget) => {
      const before = previousWidgets.get(widget.id);
      return before ? !positionsEqual(before.position, widget.position) : false;
    });
    if (positionChangedWidget) {
      const before = previousWidgets.get(positionChangedWidget.id);
      collaboration.updateWidgetPosition(
        positionChangedWidget.id,
        positionChangedWidget.position,
        before ? positionChangeMode(before.position, positionChangedWidget.position) : "move",
      );
      return;
    }
    collaboration.publishLayout(next);
  }, [collaboration]);

  const updateLayout = useCallback((next: ThreadV4LayoutJson) => {
    if (next === layout) return;
    const cloned = cloneLayout(next);
    setLayout(cloned);
    setDirty(true);
    publishLayoutDelta(layout, cloned);
  }, [layout, publishLayoutDelta]);
  const addWidget = useCallback((kind: ThreadV4WidgetKind) => updateLayout(addDashboardWidget(layout, kind)), [layout, updateLayout]);
  const removeWidget = useCallback((widgetId: string) => updateLayout({ ...layout, widgets: layout.widgets.filter((widget) => widget.id !== widgetId) }), [layout, updateLayout]);

  const save = useCallback((updateExisting: boolean) => {
    const name = currentView?.name ?? "V5 analysis dashboard";
    onSaveDashboard({
      name: updateExisting ? name : `${name} copy`,
      layout_json: layout,
      binding_json: selectedRunId ? { ...(currentView?.binding_json ?? {}), selected_run_id: selectedRunId } : currentView?.binding_json ?? {},
      widget_state_json: currentView?.widget_state_json ?? {},
      metadata: { ...(currentView?.metadata ?? {}), source: "thread-v5-dashboard" },
      sourceViewId: currentView?.id ?? null,
      updateExisting,
    });
    setDirty(false);
  }, [currentView, layout, onSaveDashboard, selectedRunId]);

  return (
    <main className="v5-main v5-dashboard-view">
      <header className="v5-page-header">
        <div>
          <span className="v5-eyebrow">Dashboard</span>
          <h1>{currentView?.name ?? "Dashboard"}</h1>
          <p>Drag cards into open gaps, resize from the corner, then save the layout.</p>
        </div>
        <div className="v5-dashboard-actions">
          {currentView && persistedProject ? (
            <span className={`v5-chip ${collaboration.status === "connected" ? "is-green" : "is-amber"}`}>
              {collaboration.status === "connected" ? (collaboration.access === "read" ? "shared read-only" : "shared editing live") : collaboration.status === "connecting" ? "shared editing connecting" : "shared editing unavailable"}
            </span>
          ) : null}
          {lockedWidgets.size ? <span className="v5-chip is-amber">{lockedWidgets.size} panel locked</span> : null}
          {dirty ? <span className="v5-chip is-amber">Unsaved layout</span> : null}
          <button className="v5-text-button" type="button" onClick={() => onOpenView("workbench")}><Icons.GridIcon size={13} />Default</button>
          <button className="v5-text-button" type="button" onClick={() => save(false)}><Icons.Save size={13} />Save as new</button>
          <button className="v5-primary-button" type="button" onClick={() => save(true)} disabled={!currentView || !dirty}><Icons.Save size={14} />Save changes</button>
        </div>
      </header>
      <div className="v5-editor-layout">
        <DashboardPalette disabled={!canEditDashboard} onAdd={addWidget} />
        <DashboardCanvas
          layout={layout}
          props={props}
          editable={canEditDashboard}
          lockedWidgets={lockedWidgets}
          onLayoutChange={updateLayout}
          onRemoveWidget={removeWidget}
          onWidgetEditing={collaboration.signalEditing}
        />
      </div>
    </main>
  );
}

export function DashboardEditorSurface(props: SurfaceProps) {
  const { selectedRunId, onSaveDashboard, onOpenView } = props;
  const [layout, setLayout] = useState<ThreadV4LayoutJson>(() => defaultDashboardLayout());
  const [name, setName] = useState("V5 analysis board");
  const updateLayout = useCallback((next: ThreadV4LayoutJson) => {
    if (next !== layout) setLayout(cloneLayout(next));
  }, [layout]);
  const addWidget = useCallback((kind: ThreadV4WidgetKind) => updateLayout(addDashboardWidget(layout, kind)), [layout, updateLayout]);
  const removeWidget = useCallback((widgetId: string) => updateLayout({ ...layout, widgets: layout.widgets.filter((widget) => widget.id !== widgetId) }), [layout, updateLayout]);
  return (
    <main className="v5-main v5-dashboard-editor">
      <header className="v5-page-header">
        <div><span className="v5-eyebrow">Board editor</span><h1>Compose a dashboard</h1><p>Add cards, drag them into gaps, and resize each one from its lower corner.</p></div>
        <button className="v5-text-button" type="button" onClick={() => onOpenView("workbench")}><Icons.Close size={13} />Exit</button>
      </header>
      <div className="v5-editor-layout">
        <DashboardPalette onAdd={addWidget} />
        <DashboardCanvas layout={layout} props={props} editable onLayoutChange={updateLayout} onRemoveWidget={removeWidget} />
      </div>
      <footer className="v5-editor-bar">
        <label className="v5-dashboard-name-input">
          <span>View name</span>
          <input value={name} onChange={(event) => setName(event.currentTarget.value)} />
        </label>
        <span>{layout.widgets.length} panels - {selectedRunId ? `bound to run ${selectedRunId}` : "project-level view"}</span>
        <button
          className="v5-primary-button"
          type="button"
          onClick={() => onSaveDashboard({
            name: name.trim() || "V5 analysis board",
            layout_json: layout,
            binding_json: selectedRunId ? { selected_run_id: selectedRunId } : {},
            metadata: { source: "thread-v5-dashboard-editor" },
          })}
        >
          <Icons.Save size={14} />Save dashboard
        </button>
      </footer>
    </main>
  );
}

export function FocusLayer({
  focus,
  flowfieldPair = null,
  data,
  projectId,
  persistedProject,
  selectedRunId,
  selectedNodeId,
  focusedAssetId,
  tableMode = "table",
  notebookEntries,
  plotLarge,
  onPlotLargeChange,
  onClose,
  onSelectRun,
  onSelectPlotPoint,
  onFullProvenance,
}: {
  focus: V5Focus | null;
  flowfieldPair?: V5FlowfieldPair;
  data: V3WorkbenchData;
  projectId: string;
  persistedProject: boolean;
  selectedRunId: string | null;
  selectedNodeId: string | null;
  focusedAssetId: string | null;
  tableMode?: V5TableMode;
  notebookEntries: ThreadV4NotebookEntry[];
  plotLarge: boolean;
  onPlotLargeChange: (large: boolean) => void;
  onClose: () => void;
  onSelectRun: SelectRunHandler;
  onSelectPlotPoint: (runId: string | null, nodeId: string | null) => void;
  onFullProvenance: () => void;
}) {
  if (!focus || focus === "notebook") return null;
  const asset = data.assets.find((item) => item.id === focusedAssetId) ?? data.assets[0] ?? null;
  const largePlotFocus = focus === "artifact" && asset?.type === "plot" && plotLarge;
  const tiledFlowfield = focus === "flowfield" && flowfieldPair !== null;
  return (
    <>
      <button className="v5-focus-scrim" type="button" aria-label="Close focus" onClick={onClose} />
      <section className={`v5-focus-panel is-${focus} ${largePlotFocus ? "is-plot-large" : ""} ${tiledFlowfield ? "is-flowfield-tiled" : ""}`}>
        <button className="v5-focus-close" type="button" onClick={onClose}><Icons.Close size={14} /></button>
        {focus === "artifact" ? <ArtifactFocus asset={asset} projectId={projectId} branch={data.branch || "main"} plotLarge={plotLarge} onPlotLargeChange={onPlotLargeChange} onSelectPlotPoint={onSelectPlotPoint} /> : null}
        {focus === "geometry" ? <GeometryFocus data={data} projectId={projectId} persistedProject={persistedProject} selectedRunId={selectedRunId} selectedNodeId={selectedNodeId} focusedAssetId={focusedAssetId} /> : null}
        {focus === "flowfield" ? (
          tiledFlowfield ? (
            <FlowfieldTiledFocus
              pair={flowfieldPair}
              data={data}
              projectId={projectId}
              persistedProject={persistedProject}
              selectedRunId={selectedRunId}
              selectedNodeId={selectedNodeId}
              focusedAssetId={focusedAssetId}
              focusedAsset={asset}
              tableMode={tableMode}
              notebookEntries={notebookEntries}
              onPlotLargeChange={onPlotLargeChange}
              onSelectRun={onSelectRun}
              onSelectPlotPoint={onSelectPlotPoint}
              onFullProvenance={onFullProvenance}
            />
          ) : (
            <FlowfieldFocus data={data} projectId={projectId} persistedProject={persistedProject} selectedRunId={selectedRunId} selectedNodeId={selectedNodeId} focusedAssetId={focusedAssetId} />
          )
        ) : null}
        {focus === "dag" ? (
          <div className="v5-focus-dag">
            {persistedProject ? (
              // FXC-8618: collapse-by-default LineageExplorer from @flexcompute/thread-graph.
              // initialDepth=1 means only the 1-hop neighbourhood around the pivot is
              // revealed on mount; the user expands via click/right-click.
              <ThreadV5LineageExplorer
                projectId={projectId}
                branch={data.branch || "main"}
                selectedNodeId={selectedNodeId}
                onNodeSelect={(nodeId) => onSelectRun(nodeId)}
                onFullProvenance={onFullProvenance}
              />
            ) : (
              // Non-persisted project: fall back to the bundle overview.
              <>
                <ThreadV5BundleProvenance data={data} selectedRunId={selectedRunId} noteCount={notebookEntries.length} onSelectRun={onSelectRun} />
                <button className="v5-primary-button" type="button" onClick={onFullProvenance}>Full provenance <Icons.ChevronRight size={14} /></button>
              </>
            )}
          </div>
        ) : null}
      </section>
    </>
  );
}

function FlowfieldFocus({ data, projectId, persistedProject, selectedRunId, selectedNodeId, focusedAssetId }: { data: V3WorkbenchData; projectId: string; persistedProject: boolean; selectedRunId: string | null; selectedNodeId: string | null; focusedAssetId: string | null }) {
  const rows = flowfieldRowsForSelection(data, selectedRunId, selectedNodeId, focusedAssetId);
  return persistedProject ? (
    <Flow360ViewerPanel
      projectId={projectId}
      branch={data.branch || "main"}
      rows={rows}
      selectionId={selectedNodeId ?? focusedAssetId ?? selectedRunId}
      entityTypes={["case"]}
    />
  ) : <FlowfieldPending />;
}

function FlowfieldTiledFocus({
  pair,
  data,
  projectId,
  persistedProject,
  selectedRunId,
  selectedNodeId,
  focusedAssetId,
  focusedAsset,
  tableMode,
  notebookEntries,
  onPlotLargeChange,
  onSelectRun,
  onSelectPlotPoint,
  onFullProvenance,
}: {
  pair: Exclude<V5FlowfieldPair, null>;
  data: V3WorkbenchData;
  projectId: string;
  persistedProject: boolean;
  selectedRunId: string | null;
  selectedNodeId: string | null;
  focusedAssetId: string | null;
  focusedAsset: ThreadAsset | null;
  tableMode: V5TableMode;
  notebookEntries: ThreadV4NotebookEntry[];
  onPlotLargeChange: (large: boolean) => void;
  onSelectRun: SelectRunHandler;
  onSelectPlotPoint: (runId: string | null, nodeId: string | null) => void;
  onFullProvenance: () => void;
}) {
  const companionTitle = flowfieldPairTitle(pair, tableMode, focusedAsset);
  return (
    <div className={`v5-flowfield-tile is-paired-${pair}`} data-testid="v5-flowfield-tile">
      <section className="v5-flowfield-main-pane" aria-label="Flowfield viewer">
        <header className="v5-flowfield-tile-header">
          <span><Icons.Flowfield size={13} />Flowfield</span>
          {selectedRunId ? <strong>{selectedRunId}</strong> : null}
        </header>
        <div className="v5-flowfield-tile-body">
          <FlowfieldFocus data={data} projectId={projectId} persistedProject={persistedProject} selectedRunId={selectedRunId} selectedNodeId={selectedNodeId} focusedAssetId={focusedAssetId} />
        </div>
      </section>
      <section className="v5-flowfield-pair-pane" aria-label={companionTitle}>
        <header className="v5-flowfield-tile-header">
          <span>{flowfieldPairIcon(pair, tableMode)}{companionTitle}</span>
        </header>
        <div className="v5-flowfield-pair-body">
          <FlowfieldPairContent
            pair={pair}
            data={data}
            projectId={projectId}
            persistedProject={persistedProject}
            selectedRunId={selectedRunId}
            selectedNodeId={selectedNodeId}
            focusedAssetId={focusedAssetId}
            focusedAsset={focusedAsset}
            tableMode={tableMode}
            notebookEntries={notebookEntries}
            onPlotLargeChange={onPlotLargeChange}
            onSelectRun={onSelectRun}
            onSelectPlotPoint={onSelectPlotPoint}
            onFullProvenance={onFullProvenance}
          />
        </div>
      </section>
    </div>
  );
}

function FlowfieldPairContent({
  pair,
  data,
  projectId,
  persistedProject,
  selectedRunId,
  selectedNodeId,
  focusedAssetId,
  focusedAsset,
  tableMode,
  notebookEntries,
  onPlotLargeChange,
  onSelectRun,
  onSelectPlotPoint,
  onFullProvenance,
}: {
  pair: Exclude<V5FlowfieldPair, null>;
  data: V3WorkbenchData;
  projectId: string;
  persistedProject: boolean;
  selectedRunId: string | null;
  selectedNodeId: string | null;
  focusedAssetId: string | null;
  focusedAsset: ThreadAsset | null;
  tableMode: V5TableMode;
  notebookEntries: ThreadV4NotebookEntry[];
  onPlotLargeChange: (large: boolean) => void;
  onSelectRun: SelectRunHandler;
  onSelectPlotPoint: (runId: string | null, nodeId: string | null) => void;
  onFullProvenance: () => void;
}) {
  if (pair === "artifact") {
    return (
      <ArtifactFocus
        asset={focusedAsset}
        projectId={projectId}
        branch={data.branch || "main"}
        plotLarge
        onPlotLargeChange={onPlotLargeChange}
        onSelectPlotPoint={onSelectPlotPoint}
      />
    );
  }
  if (pair === "geometry") {
    return <GeometryFocus data={data} projectId={projectId} persistedProject={persistedProject} selectedRunId={selectedRunId} selectedNodeId={selectedNodeId} focusedAssetId={focusedAssetId} />;
  }
  if (pair === "dag") {
    return (
      <div className="v5-focus-dag">
        {persistedProject ? (
          // FXC-8618: collapse-by-default explorer in the tiled flowfield view.
          <ThreadV5LineageExplorer
            projectId={projectId}
            branch={data.branch || "main"}
            selectedNodeId={selectedNodeId}
            onNodeSelect={(nodeId) => onSelectRun(nodeId)}
            onFullProvenance={onFullProvenance}
          />
        ) : (
          <>
            <ThreadV5BundleProvenance data={data} selectedRunId={selectedRunId} noteCount={notebookEntries.length} onSelectRun={onSelectRun} />
            <button className="v5-primary-button" type="button" onClick={onFullProvenance}>Full provenance <Icons.ChevronRight size={14} /></button>
          </>
        )}
      </div>
    );
  }
  return <RunsTable data={data} selectedRunId={selectedRunId} onSelectRun={onSelectRun} mode={pair === "correlogram" ? "correlogram" : tableMode} />;
}

function flowfieldPairTitle(pair: Exclude<V5FlowfieldPair, null>, tableMode: V5TableMode, focusedAsset: ThreadAsset | null): string {
  if (pair === "artifact") return focusedAsset?.label ?? "Plot";
  if (pair === "geometry") return "Geometry";
  if (pair === "dag") return "DAG";
  if (pair === "correlogram" || tableMode === "correlogram") return "Correlogram";
  return "Simulation runs";
}

function flowfieldPairIcon(pair: Exclude<V5FlowfieldPair, null>, tableMode: V5TableMode): ReactNode {
  if (pair === "artifact") return <Icons.SavedPlots size={13} />;
  if (pair === "geometry") return <Icons.Cube size={13} />;
  if (pair === "dag") return <Icons.Dag size={13} />;
  if (pair === "correlogram" || tableMode === "correlogram") return <Icons.SavedPlots size={13} />;
  return <Icons.Table size={13} />;
}

export function NotebookDrawer({ selectedRunId, entries, draft, onDraftChange, onAdd, onClose }: NotebookProps) {
  const filtered = selectedRunId
    ? entries.filter((entry) => JSON.stringify(entry.anchors ?? {}).includes(selectedRunId) || entry.body.includes(selectedRunId)).concat(entries.filter((entry) => !JSON.stringify(entry.anchors ?? {}).includes(selectedRunId) && !entry.body.includes(selectedRunId)).slice(0, 3))
    : entries;
  return (
    <aside className="v5-spool">
      <header><span className="v5-eyebrow">Spool</span><button className="v5-icon-button" type="button" onClick={onClose}><Icons.Close size={13} /></button></header>
      <strong>{selectedRunId ? `Context: run ${selectedRunId}` : "Project memory"}</strong>
      <div className="v5-note-list">
        {filtered.length ? filtered.slice(0, 12).map((entry) => (
          <article key={entry.id} className={`v5-note is-${entry.entry_type}`}>
            <span>{entry.author_label || entry.entry_type} - {entry.source_type}</span>
            <p>{entry.body}</p>
          </article>
        )) : <p className="v5-muted">Notes, agent observations, and source links will collect here.</p>}
      </div>
      <label className="v5-note-composer">
        <span>Add note</span>
        <textarea value={draft} onChange={(event) => onDraftChange(event.currentTarget.value)} placeholder={selectedRunId ? `Linked to run ${selectedRunId}` : "Project note"} />
      </label>
      <button className="v5-primary-button" type="button" onClick={onAdd} disabled={!draft.trim()}><Icons.Plus size={14} />Add to Spool</button>
    </aside>
  );
}

export function ProjectChatDrawer({ projectName, status, streamStatus, messages, draft, sending, typingUsers, error, onDraftChange, onTyping, onSend, onClose }: ProjectChatDrawerProps) {
  const canSend = status === "ready" && !sending && Boolean(draft.trim());
  const activeTypingUsers = typingUsers.slice(0, 3).map((event) => event.display_name || event.user_id);

  return (
    <aside className="v5-chat-drawer">
      <header>
        <span className="v5-eyebrow">Project chat</span>
        <button className="v5-icon-button" type="button" onClick={onClose} aria-label="Close project chat"><Icons.Close size={13} /></button>
      </header>
      <div className="v5-chat-heading">
        <strong>{projectName}</strong>
        <span className={`v5-live ${streamStatus === "connected" ? "is-connected" : "is-pending"}`}><i />{chatStatusLabel(status, streamStatus)}</span>
      </div>
      <div className="v5-chat-list">
        {messages.length ? messages.map((message) => (
          <article key={message.id} className="v5-chat-message">
            <header>
              <span>{identityInitials(message.author_id)}</span>
              <strong>{message.author_id}</strong>
              <time dateTime={message.created_at}>{chatTimeLabel(message.created_at)}</time>
            </header>
            <p>{message.body}</p>
          </article>
        )) : (
          <p className="v5-muted">No project messages yet.</p>
        )}
      </div>
      {activeTypingUsers.length ? (
        <div className="v5-chat-typing">{activeTypingUsers.join(", ")} typing...</div>
      ) : null}
      {error ? <p className="v5-chat-error">{error}</p> : null}
      <form
        className="v5-chat-composer"
        onSubmit={(event) => {
          event.preventDefault();
          if (canSend) void onSend();
        }}
      >
        <textarea
          value={draft}
          onChange={(event) => {
            onDraftChange(event.currentTarget.value);
            onTyping();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              if (canSend) void onSend();
            }
          }}
          placeholder="Message this project"
        />
        <button className="v5-primary-button" type="submit" disabled={!canSend}>
          <Icons.Comments size={14} />{sending ? "Sending" : "Send"}
        </button>
      </form>
    </aside>
  );
}

function Panel({ title, eyebrow, actions, children }: { title: string; eyebrow?: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="v5-panel">
      <header className="v5-panel-header">
        <div><span className="v5-eyebrow">{eyebrow}</span><h2>{title}</h2></div>
        {actions ? <div className="v5-panel-actions">{actions}</div> : null}
      </header>
      <div className="v5-panel-body">{children}</div>
    </section>
  );
}

function TableToggle({ tableMode, onChange }: { tableMode: V5TableMode; onChange: (mode: V5TableMode) => void }) {
  return (
    <div className="v5-toggle">
      <button className={tableMode === "table" ? "is-active" : ""} type="button" onClick={() => onChange("table")}><Icons.Table size={13} />Table</button>
      <button className={tableMode === "correlogram" ? "is-active" : ""} type="button" onClick={() => onChange("correlogram")}><Icons.Plot size={13} />Corr</button>
    </div>
  );
}

export function RunsTable({ data, selectedRunId, onSelectRun, mode }: { data: V3WorkbenchData; selectedRunId: string | null; onSelectRun: SelectRunHandler; mode: V5TableMode }) {
  const [filteredRowIds, setFilteredRowIds] = useState<Set<string> | null>(null);
  const filterActive = Boolean(filteredRowIds);

  if (mode === "correlogram") {
    return (
      <RunsCorrelogram
        data={data}
        selectedRunId={selectedRunId}
        filteredRowIds={filteredRowIds}
        filterActive={filterActive}
        onSelectRun={onSelectRun}
      />
    );
  }
  if (!data.rows.length) {
    const hasGeometry = data.provenance.nodes.some((node) => node.type === "geometry" || node.type === "mesh");
    return <EmptyProjectState projectName={data.project?.name ?? "Thread project"} persistedProject hasGeometry={hasGeometry} reason={data.errors[0] ?? null} />;
  }
  return (
    <AdvancedRunsTable
      data={data}
      selectedRunId={selectedRunId}
      onSelectRun={onSelectRun}
      onFilterRowsChange={setFilteredRowIds}
    />
  );
}

function AdvancedRunsTable({
  data,
  selectedRunId,
  onSelectRun,
  onFilterRowsChange,
}: {
  data: V3WorkbenchData;
  selectedRunId: string | null;
  onSelectRun: SelectRunHandler;
  onFilterRowsChange: (rowIds: Set<string> | null) => void;
}) {
  const gridApiRef = useRef<GridApi<ThreadRow> | null>(null);
  const lastPublishedFilterRef = useRef<string>("all");
  const gridTheme = useMemo(
    () => themeQuartz.withParams({
      accentColor: "#4f9f77",
      backgroundColor: "rgba(250, 250, 247, 0.96)",
      borderColor: "rgba(29, 32, 29, 0.12)",
      browserColorScheme: "light",
      cellHorizontalPadding: 10,
      chromeBackgroundColor: "rgba(243, 242, 236, 0.96)",
      fontFamily: "var(--v5-mono)",
      foregroundColor: "#20231f",
      headerBackgroundColor: "rgba(237, 235, 228, 0.96)",
      headerFontSize: 11,
      oddRowBackgroundColor: "rgba(247, 246, 240, 0.82)",
      rowHoverColor: "rgba(79, 159, 119, 0.1)",
      selectedRowBackgroundColor: "rgba(79, 159, 119, 0.16)",
      wrapperBorderRadius: 0,
    }),
    [],
  );
  const [advancedFilterParent, setAdvancedFilterParent] = useState<HTMLDivElement | null>(null);
  const columns = useMemo(() => columnsWithFallback(data), [data]);
  const columnDefs = useMemo(() => columnsForV5Grid(columns, data.rows), [columns, data.rows]);

  const syncSelectedRow = useCallback((api: GridApi<ThreadRow>) => {
    api.forEachNode((node) => {
      if (!node.data) return;
      node.setSelected(Boolean(selectedRunId && rowKey(node.data) === selectedRunId));
    });
  }, [selectedRunId]);

  const publishFilteredRows = useCallback((api: GridApi<ThreadRow>) => {
    const rowIds = new Set<string>();
    api.forEachNodeAfterFilterAndSort((node) => {
      if (node.data) rowIds.add(rowKey(node.data));
    });
    const next = api.isAnyFilterPresent() ? rowIds : null;
    const signature = next ? [...next].sort().join("\u001f") : "all";
    if (signature === lastPublishedFilterRef.current) return;
    lastPublishedFilterRef.current = signature;
    onFilterRowsChange(next);
  }, [onFilterRowsChange]);

  useEffect(() => {
    if (!gridApiRef.current) return;
    syncSelectedRow(gridApiRef.current);
  }, [selectedRunId, syncSelectedRow]);

  return (
    <div className="v5-run-table v5-ag-grid-wrap" data-testid="v5-ag-grid">
      <div className="v5-grid-toolbar">
        <span><Icons.Link size={12} />DAG projection</span>
        <div ref={setAdvancedFilterParent} className="v5-advanced-filter-host" data-testid="v5-advanced-filter-host" />
        <span className="v5-grid-count">{data.totalRowCount || data.rows.length} rows · {columns.length} fields</span>
      </div>
      <AgGridReact<ThreadRow>
        theme={gridTheme}
        rowData={data.rows}
        columnDefs={columnDefs}
        enableAdvancedFilter={true}
        includeHiddenColumnsInAdvancedFilter={true}
        advancedFilterParent={advancedFilterParent}
        getRowId={(params) => rowKey(params.data)}
        getRowClass={(params) => params.data ? rowClassForV5(params.data, selectedRunId) : ""}
        defaultColDef={{
          cellDataType: false,
          enableRowGroup: true,
          enableValue: true,
          filter: true,
          resizable: true,
          sortable: true,
        }}
        autoGroupColumnDef={{
          headerName: "Family / operation rail",
          minWidth: 190,
          pinned: "left",
          cellRendererParams: { suppressCount: false },
        }}
        groupDisplayType="singleColumn"
        groupDefaultExpanded={1}
        rowGroupPanelShow="always"
        suppressAggFuncInHeader={false}
        maintainColumnOrder={true}
        rowSelection={{
          mode: "singleRow",
          checkboxes: false,
          enableClickSelection: true,
        }}
        animateRows={false}
        suppressCellFocus={false}
        onGridReady={(event: GridReadyEvent<ThreadRow>) => {
          gridApiRef.current = event.api;
          publishFilteredRows(event.api);
          syncSelectedRow(event.api);
        }}
        onFilterChanged={(event: FilterChangedEvent<ThreadRow>) => publishFilteredRows(event.api)}
        onModelUpdated={(event: ModelUpdatedEvent<ThreadRow>) => {
          publishFilteredRows(event.api);
          syncSelectedRow(event.api);
        }}
        onCellClicked={(event: CellClickedEvent<ThreadRow>) => {
          if (!event.data) return;
          const columnId = event.column?.getColId?.() ?? (typeof event.colDef?.field === "string" ? event.colDef.field : null);
          const nodeId = columnId ? event.data.cellNodeIds?.[columnId] ?? event.data.rowNodeId ?? null : event.data.rowNodeId ?? null;
          onSelectRun(rowKey(event.data), nodeId);
        }}
      />
    </div>
  );
}

function RunsCorrelogram({
  data,
  selectedRunId,
  filteredRowIds,
  filterActive,
  onSelectRun,
}: {
  data: V3WorkbenchData;
  selectedRunId: string | null;
  filteredRowIds: ReadonlySet<string> | null;
  filterActive: boolean;
  onSelectRun: SelectRunHandler;
}) {
  const rows = useMemo(
    () => filteredRowIds ? data.rows.filter((row) => filteredRowIds.has(rowKey(row))) : data.rows,
    [data.rows, filteredRowIds],
  );
  const correlogramRows = rows as unknown as Record<string, unknown>[];
  const allNumericColumns = useMemo<CorrelogramColumn[]>(() => {
    return columnsWithFallback({ ...data, rows })
      .filter((column) => rows.some((row) => typeof row[column.id] === "number" && Number.isFinite(row[column.id] as number)))
      .map((column) => {
        const role = correlogramRole(column);
        const evidence = correlogramEvidence(column);
        return {
          key: column.id,
          header: role === "condition" ? `${column.label} rail` : column.label,
          role,
          evidence,
          source: evidence === "model-conditioned" ? "model" : role === "derived" ? "derived" : "observed",
        };
      });
  }, [data, rows]);
  const zAxisKey = useMemo(() => preferredCorrelogramZAxis(allNumericColumns), [allNumericColumns]);
  const numericColumns = useMemo<CorrelogramColumn[]>(() => {
    if (!zAxisKey || allNumericColumns.length <= V5_CORRELOGRAM_MAX_COLUMNS) return allNumericColumns;
    const byKey = new Map(allNumericColumns.map((column) => [column.key, column]));
    const ranked = rankColumnsByCorrelationTo(
      correlogramRows,
      allNumericColumns.map((column) => ({ field: column.key, headerName: column.header })),
      zAxisKey,
    );
    const rankedFields = ranked.map((score) => score.field);
    const conditionKeys = allNumericColumns
      .filter((column) => column.role === "condition" && column.key !== zAxisKey)
      .sort((left, right) => rankedIndex(rankedFields, left.key) - rankedIndex(rankedFields, right.key))
      .map((column) => column.key)
      .slice(0, 4);
    const responseKeys = rankedFields
      .filter((field) => !conditionKeys.includes(field))
      .slice(0, Math.max(0, V5_CORRELOGRAM_MAX_COLUMNS - 1 - conditionKeys.length));
    return [...new Set([zAxisKey, ...conditionKeys, ...responseKeys, ...allNumericColumns.map((column) => column.key)])]
      .map((key) => byKey.get(key))
      .filter((column): column is CorrelogramColumn => Boolean(column))
      .slice(0, V5_CORRELOGRAM_MAX_COLUMNS);
  }, [allNumericColumns, correlogramRows, zAxisKey]);
  const selectedRowIdx = selectedRunId ? rows.findIndex((row) => rowKey(row) === selectedRunId || row.runId === selectedRunId) : -1;
  const {
    menu: pointMenu,
    close: closePointMenu,
    setHoveredPoint,
    handleContextMenu,
  } = useDataPointContextMenu(correlogramRows);
  const groups = useMemo(() => {
    const grouped = new Map<string, Record<string, unknown>[]>();
    for (const row of correlogramRows) {
      const key = typeof row.family === "string" && row.family ? row.family : "runs";
      grouped.set(key, [...(grouped.get(key) ?? []), row]);
    }
    return [...grouped.entries()].map(([key, groupRows], index) => ({
      key,
      label: data.families[key]?.label ?? key,
      rows: groupRows,
      color: V5_CORRELOGRAM_GROUP_COLORS[index % V5_CORRELOGRAM_GROUP_COLORS.length]!,
    }));
  }, [correlogramRows, data.families]);
  const quantityCount = numericColumns.filter((column) => column.role === "response" || column.role === "derived").length;
  const conditionCount = numericColumns.filter((column) => column.role === "condition").length;
  const hiddenNumericCount = Math.max(0, allNumericColumns.length - numericColumns.length);
  const zAxisLabel = zAxisKey ? numericColumns.find((column) => column.key === zAxisKey)?.header ?? zAxisKey : undefined;

  if (!rows.length || numericColumns.length < 2) {
    return (
      <div className="v5-correlogram v5-correlogram-empty" data-testid="v5-correlogram">
        <strong>No plottable correlation view</strong>
        <span>Thread needs at least two numeric condition or output fields in this graph projection.</span>
      </div>
    );
  }

  return (
    <div className="v5-correlogram" data-testid="v5-correlogram">
      <div className="v5-correlogram-bar">
        <span>{filterActive ? `${rows.length.toLocaleString()} / ${data.rows.length.toLocaleString()} filtered rows` : `${rows.length.toLocaleString()} rows`}</span>
        <span>{conditionCount} conditions</span>
        <span>{quantityCount} outputs</span>
        {zAxisLabel ? <span>Z {zAxisLabel}</span> : null}
        {hiddenNumericCount ? <span>{hiddenNumericCount} ranked off-screen</span> : null}
      </div>
      <div className="v5-correlogram-host" data-testid="v5-existing-correlogram-host">
        <Suspense fallback={<div className="v5-correlogram-loading">Loading correlogram projection...</div>}>
          <DataExplorerCorrelogram
            rows={correlogramRows}
            numericColumns={numericColumns}
            groups={groups.length > 1 ? groups : undefined}
            colorByKey={groups.length > 1 ? undefined : "family"}
            zAxisKey={zAxisKey}
            selectedRowIdx={selectedRowIdx >= 0 ? selectedRowIdx : undefined}
            onPointClick={(rowIdx) => {
              const row = rows[rowIdx];
              if (row) onSelectRun(rowKey(row), row.rowNodeId ?? null);
            }}
            onPointContextMenu={(rowIdx, event, context) => {
              const row = rows[rowIdx];
              if (!row) return;
              setHoveredPoint(row.rowNodeId ?? rowKey(row), row as unknown as Record<string, unknown>, context);
              handleContextMenu(event);
            }}
          />
        </Suspense>
      </div>
      {pointMenu ? <DataPointContextMenu menu={pointMenu} onClose={closePointMenu} /> : null}
    </div>
  );
}

function normalizedColumnId(id: string): string {
  return id.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_");
}

function readableColumnLabel(id: string): string {
  const known: Record<string, string> = {
    runId: "Run",
    family: "Family",
    alpha: "Alpha",
    beta: "Beta",
    mach: "Mach",
    mesh: "Mesh",
    design: "Design",
    status: "Status",
  };
  return known[id] ?? id.replace(/^_+/, "").replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function isNumericColumn(rows: readonly ThreadRow[], columnId: string): boolean {
  return rows.some((row) => typeof row[columnId] === "number" && Number.isFinite(row[columnId] as number));
}

function columnsWithFallback(data: Pick<V3WorkbenchData, "columns" | "rows">): ThreadColumn[] {
  const columns = data.columns.length ? [...data.columns] : [];
  const seen = new Set(columns.map((column) => column.id));
  const preferred = ["runId", "family", "alpha", "beta", "mach", "mesh", "design", "CL", "cl", "CD", "cd", "Cm", "cm", "status"];
  const rowFields = new Set<string>();
  for (const row of data.rows.slice(0, 200)) {
    for (const key of Object.keys(row)) {
      if (key === "cellNodeIds" || key === "rowNodeId") continue;
      rowFields.add(key);
    }
  }
  for (const id of [...preferred, ...rowFields]) {
    if (seen.has(id) || !rowFields.has(id)) continue;
    columns.push({
      id,
      label: readableColumnLabel(id),
      kind: inferColumnKind(id, data.rows),
    });
    seen.add(id);
  }
  return columns;
}

function inferColumnKind(id: string, rows: readonly ThreadRow[]): ThreadColumn["kind"] {
  const normalized = normalizedColumnId(id);
  if (normalized === "runid" || normalized === "run_id" || normalized === "name") return "id";
  if (normalized === "family" || normalized.includes("campaign")) return "family";
  if (/^(alpha|beta|mach|re|reynolds|aoa|altitude|theta_ht|t_mult|thrust_mult|gap_fraction)$/.test(normalized)) return "condition";
  if (normalized.includes("asset") || normalized.includes("plot") || normalized.includes("uvf")) return "asset";
  if (isNumericColumn(rows, id) && /^(cl|cd|cm|cy|cn|cf|ld|c_l|c_d|c_m)/.test(normalized)) return "quantity";
  return isNumericColumn(rows, id) ? "quantity" : "metadata";
}

function columnGroupForV5(column: ThreadColumn): "setup" | "conditions" | "outputs" | "artifacts" | "metadata" {
  const id = normalizedColumnId(column.id);
  if (column.kind === "condition" || /^(alpha|beta|mach|re|reynolds|aoa|altitude|theta_ht|t_mult|thrust_mult|gap_fraction)$/.test(id)) return "conditions";
  if (column.kind === "quantity") return "outputs";
  if (column.kind === "asset") return "artifacts";
  if (column.kind === "id" || column.kind === "family" || ["runid", "run_id", "name", "family", "design", "mesh", "script", "status", "updated"].includes(id)) return "setup";
  return "metadata";
}

function defaultRowGroupForV5(column: ThreadColumn): boolean {
  const id = normalizedColumnId(column.id);
  return column.kind === "family" || id === "family" || id === "tsangpo_campaign_id" || id === "sweep_group";
}

function aggregationForV5Column(column: ThreadColumn, rows: readonly ThreadRow[]): Exclude<ColDef<ThreadRow>["aggFunc"], null> | undefined {
  if (column.kind !== "quantity" && !isNumericColumn(rows, column.id)) return undefined;
  const id = normalizedColumnId(column.id);
  const label = normalizedColumnId(column.label);
  if (id === "cd" || id === "c_d" || label.includes("drag")) return "min";
  if (id === "ld" || id === "l_d" || id === "l_over_d" || label.includes("lift_drag")) return "max";
  if (id.includes("residual") || id.includes("error")) return "min";
  return "avg";
}

function columnsForVisibleV5Grid(columns: readonly ThreadColumn[]): ThreadColumn[] {
  const byGroup = {
    setup: columns.filter((column) => columnGroupForV5(column) === "setup"),
    conditions: columns.filter((column) => columnGroupForV5(column) === "conditions"),
    outputs: columns.filter((column) => columnGroupForV5(column) === "outputs"),
    artifacts: columns.filter((column) => columnGroupForV5(column) === "artifacts"),
    metadata: columns.filter((column) => columnGroupForV5(column) === "metadata"),
  };
  return [
    ...byGroup.setup.slice(0, 7),
    ...byGroup.conditions.slice(0, 9),
    ...byGroup.outputs.slice(0, 14),
    ...byGroup.artifacts.slice(0, 3),
    ...byGroup.metadata.slice(0, 5),
  ];
}

function columnsForV5Grid(columns: readonly ThreadColumn[], rows: readonly ThreadRow[]): V5GridColumnDef[] {
  const visibleColumns = columnsForVisibleV5Grid(columns);
  const leafDefs: V5GridColDef[] = visibleColumns.map((column) => ({
    field: column.id,
    headerName: column.label,
    sortable: true,
    filter: isNumericColumn(rows, column.id) ? "agNumberColumnFilter" : true,
    resizable: true,
    enableRowGroup: column.kind !== "quantity" && column.kind !== "asset",
    enableValue: column.kind === "quantity" || isNumericColumn(rows, column.id),
    initialRowGroup: defaultRowGroupForV5(column),
    initialHide: defaultRowGroupForV5(column),
    minWidth: column.kind === "id" ? 150 : column.kind === "quantity" ? 112 : 98,
    type: isNumericColumn(rows, column.id) ? "numericColumn" : undefined,
    initialAggFunc: aggregationForV5Column(column, rows),
    allowedAggFuncs: isNumericColumn(rows, column.id) ? ["avg", "min", "max", "sum", "count"] : ["count"],
    headerClass: `v5-grid-header-${columnGroupForV5(column)}`,
    cellClass: (params) => [
      `v5-grid-cell-${columnGroupForV5(column)}`,
      params.data?.cellNodeIds?.[column.id] ? "v5-grid-cell-has-node" : "",
    ].filter(Boolean).join(" "),
    valueFormatter: (params: ValueFormatterParams<ThreadRow, unknown>) => formatValue(params.value),
    cellRenderer: (params: ICellRendererParams<ThreadRow, unknown>) => {
      const nodeId = params.data?.cellNodeIds?.[column.id];
      const runId = params.data ? rowKey(params.data) : undefined;
      return (
        <span
          className="v5-cell-handle"
          data-node-id={nodeId}
          data-row-node-id={params.data?.rowNodeId}
          data-row-id={runId}
          data-col-id={column.id}
          title={nodeId ? `${nodeId} -> ${column.label}` : column.label}
        >
          {formatValue(params.value)}
        </span>
      );
    },
  }));

  const columnById = new Map(visibleColumns.map((column) => [column.id, column]));
  const byGroup = {
    conditions: leafDefs.filter((column) => columnGroupForV5(columnById.get(String(column.field))!) === "conditions"),
    outputs: leafDefs.filter((column) => columnGroupForV5(columnById.get(String(column.field))!) === "outputs"),
    setup: leafDefs.filter((column) => columnGroupForV5(columnById.get(String(column.field))!) === "setup"),
    artifacts: leafDefs.filter((column) => columnGroupForV5(columnById.get(String(column.field))!) === "artifacts"),
    metadata: leafDefs.filter((column) => columnGroupForV5(columnById.get(String(column.field))!) === "metadata"),
  };
  const groups: V5GridColumnDef[] = [];
  const addGroup = (headerName: string, headerClass: string, children: V5GridColDef[]) => {
    if (children.length) groups.push({ headerName, headerClass, marryChildren: true, children });
  };
  addGroup("Conditions", "v5-grid-group-conditions", byGroup.conditions);
  addGroup("Outputs", "v5-grid-group-outputs", byGroup.outputs);
  addGroup("Run + operation", "v5-grid-group-setup", byGroup.setup);
  addGroup("Artifacts", "v5-grid-group-artifacts", byGroup.artifacts);
  addGroup("Metadata", "v5-grid-group-metadata", byGroup.metadata);
  return groups;
}

function rowClassForV5(row: ThreadRow, selectedRunId: string | null): string {
  if (!selectedRunId) return "";
  return rowKey(row) === selectedRunId || row.runId === selectedRunId ? "v5-grid-row-selected" : "";
}

function correlogramToken(value: string | undefined): string {
  return String(value ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function correlogramRole(column: ThreadColumn): CorrelogramColumn["role"] {
  const token = correlogramToken(`${column.id} ${column.label}`);
  if (column.kind === "condition") return "condition";
  if (/(alpha|beta|omega|derivative|stability|avl)$/.test(token) || token.includes("alphaavl") || token.includes("betaavl")) return "derived";
  if (column.kind === "quantity") return "response";
  if (/(^|[^a-z])(cl|cd|cy|cf|cm|cn|ld)/.test(token) || /pressure|friction|coefficient/.test(token)) return "response";
  if (/alpha|beta|mach|reynolds|rpm|thrust|elevator|aileron|theta|gap/.test(token)) return "condition";
  return "metadata";
}

function correlogramEvidence(column: ThreadColumn): CorrelogramColumn["evidence"] {
  const token = correlogramToken(`${column.id} ${column.label}`);
  return /model|surrogate|corrected|transport|predicted|prediction|teacher/.test(token)
    ? "model-conditioned"
    : "observed";
}

function preferredCorrelogramZAxis(columns: readonly CorrelogramColumn[]): string | undefined {
  for (const priority of V5_CORRELOGRAM_RESPONSE_PRIORITY) {
    const match = columns.find((column) => correlogramToken(column.key) === priority || correlogramToken(column.header) === priority);
    if (match) return match.key;
  }
  return (
    columns.find((column) => column.role === "response")?.key ??
    columns.find((column) => column.role === "derived")?.key ??
    columns.find((column) => column.role === "condition")?.key ??
    columns[0]?.key
  );
}

function rankedIndex(fields: readonly string[], key: string): number {
  const index = fields.indexOf(key);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

function RunList({ rows, selectedRunId, onSelectRun }: { rows: ThreadRow[]; selectedRunId: string | null; onSelectRun: SelectRunHandler }) {
  return (
    <div className="v5-run-body">
      {rows.slice(0, 80).map((row) => {
        const id = rowKey(row);
        return (
          <button key={id} className={`v5-run-row ${selectedRunId === id ? "is-selected" : ""}`} type="button" onClick={() => onSelectRun(id, row.rowNodeId ?? null)}>
            <strong>{row.runId || id}</strong>
            <span>{formatValue(row.alpha)}</span>
            <span>{formatValue(row.beta)}</span>
            <span>{formatValue(row.mach)}</span>
            <span>{formatValue(row.mesh)}</span>
            <span>{formatValue(valueFor(row, ["CL", "cl", "c_l"]))}</span>
            <span>{formatValue(valueFor(row, ["CD", "cd", "c_d"]))}</span>
            <span>{formatValue(valueFor(row, ["Cm", "CM", "cm", "c_m"]))}</span>
            <em className={`is-${rowState(row)}`}>{String(row.status ?? row.state ?? rowState(row))}</em>
          </button>
        );
      })}
    </div>
  );
}

function ArtifactShelf({ assets, loading, selectedRunId, focusedAssetId, onOpenArtifact }: { assets: ThreadAsset[]; loading: boolean; selectedRunId: string | null; focusedAssetId: string | null; onOpenArtifact: (assetId: string) => void }) {
  return (
    <Panel title="Artifacts" eyebrow={loading ? "loading" : `${assets.length} assets`}>
      {loading ? <CardLoadingPanel label="Loading artifacts" /> : assets.length ? <ArtifactList assets={assets} selectedRunId={selectedRunId} focusedAssetId={focusedAssetId} onOpenArtifact={onOpenArtifact} /> : <EmptyShelf />}
    </Panel>
  );
}

function ArtifactList({ assets, selectedRunId, focusedAssetId, onOpenArtifact }: { assets: ThreadAsset[]; selectedRunId: string | null; focusedAssetId?: string | null; onOpenArtifact: (assetId: string) => void }) {
  return (
    <div className="v5-artifact-list">
      {assets.slice(0, 40).map((asset) => {
        const linked = assetLinkedToRun(asset, selectedRunId);
        return (
          <button key={asset.id} className={`v5-artifact-card ${linked ? "is-linked" : ""} ${focusedAssetId === asset.id ? "is-selected" : ""}`} type="button" onClick={() => onOpenArtifact(asset.id)}>
            <ArtifactThumb asset={asset} />
            <span><strong>{asset.label}</strong><small>{asset.type} - {asset.updated}</small></span>
          </button>
        );
      })}
    </div>
  );
}

function ArtifactDeck({ kind, assets, selectedRunId, focusedAssetId, onOpen }: { kind: string; assets: ThreadAsset[]; selectedRunId: string | null; focusedAssetId: string | null; onOpen: (assetId: string) => void }) {
  const top = assets.find((asset) => assetLinkedToRun(asset, selectedRunId)) ?? assets[0]!;
  return (
    <section className={`v5-deck ${assets.some((asset) => asset.id === focusedAssetId) ? "is-selected" : ""}`}>
      <div className="v5-card-stack">
        {assets.slice(0, 5).map((asset, index) => <span key={asset.id} className={`v5-stack-card n-${index}`}><ArtifactThumb asset={asset} /></span>)}
      </div>
      <strong>{kindLabel(kind)}</strong>
      <span>{assets.length} cards</span>
      <button type="button" onClick={() => onOpen(top.id)}>Open</button>
    </section>
  );
}

function ArtifactFocus({ asset, projectId, branch, plotLarge, onPlotLargeChange, onSelectPlotPoint }: { asset: ThreadAsset | null; projectId: string; branch: string; plotLarge: boolean; onPlotLargeChange: (large: boolean) => void; onSelectPlotPoint: (runId: string | null, nodeId: string | null) => void }) {
  if (!asset) return <EmptyShelf />;
  return (
    <div className="v5-artifact-focus">
      <div className="v5-artifact-stage">
        {asset.type === "plot" ? <ThreadPlotFocus asset={asset} projectId={projectId} branch={branch} plotLarge={plotLarge} onPlotLargeChange={onPlotLargeChange} onSelectPlotPoint={onSelectPlotPoint} /> : <ArtifactThumb asset={asset} large />}
      </div>
      <div>
        <span className="v5-eyebrow">{asset.type}</span>
        <h2>{asset.label}</h2>
        <p>{asset.summary || asset.sub || "Linked Thread artifact."}</p>
        <dl>
          <div><dt>Source</dt><dd>{asset.source}</dd></div>
          <div><dt>Rows</dt><dd>{asset.refs.rows?.length ?? 0}</dd></div>
          <div><dt>Health</dt><dd>{asset.health}</dd></div>
          <div><dt>Updated</dt><dd>{asset.updated}</dd></div>
        </dl>
      </div>
    </div>
  );
}

const V5_PLOT_ENGINES = new Set<PlotEngine>(["plotly", "recharts", "ag-charts", "scichart"]);

type CollectionMemberRef = {
  plotId?: string;
  path?: string;
  title?: string;
  chartType?: string;
};

function stringField(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function plotRows(plot: SavedPlot | null | undefined): Record<string, unknown>[] {
  return Array.isArray(plot?.data_snapshot) ? plot.data_snapshot : [];
}

function plotRowsWithNodeFallback(plot: SavedPlot, rows: Record<string, unknown>[]): Record<string, unknown>[] {
  const sourceIds = Array.isArray(plot.source_node_ids) ? plot.source_node_ids : [];
  const hasNodeIds = rows.some((row) => typeof row._node_id === "string" && row._node_id.trim());
  if (hasNodeIds) return rows;
  return rows.map((row, index) => ({
    ...row,
    _node_id: sourceIds[index] ?? plotPointNodeId(row) ?? plotPointRunId(row) ?? row._node_id,
  }));
}

function plotPointNodeId(row: Record<string, unknown>): string | null {
  for (const key of ["_node_id", "node_id", "rowNodeId", "source_node_id"]) {
    const value = row[key];
    if (typeof value === "string" && value.trim()) return value;
  }
  return null;
}

function plotPointRunId(row: Record<string, unknown>): string | null {
  for (const key of ["runId", "flat_view_row_id", "joined_row_id", "analysis_row_id", "row_id", "_row_id", "name", "case_id", "flow360_case_id", "sample_id", "id"]) {
    const value = row[key];
    if (typeof value === "string" && value.trim()) return value;
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }
  return null;
}

function plotNativeConfig(plot: SavedPlot | null | undefined): PlotBuilderConfig | null {
  if (!plot || !isRecord(plot.config)) return null;
  const raw = plot.config;
  const engine = stringField(raw.engine);
  if (!engine || !V5_PLOT_ENGINES.has(engine as PlotEngine)) return null;
  const chartType = stringField(raw.chartType) ?? plot.chart_type;
  if (!chartType || chartType === "image" || chartType === "plot_collection") return null;
  const xAxis = isRecord(raw.xAxis) ? raw.xAxis : {};
  const yAxis = isRecord(raw.yAxis) ? raw.yAxis : {};
  const xField = stringField(xAxis.field) ?? "x";
  const yField = stringField(yAxis.field) ?? "y";
  if (!xField || !yField) return null;

  const defaults = createDefaultConfig();
  return {
    ...defaults,
    ...raw,
    chartType: chartType as PlotBuilderConfig["chartType"],
    engine: engine as PlotEngine,
    dataSource: "full",
    xAxis: { ...defaults.xAxis, ...xAxis, field: xField },
    yAxis: { ...defaults.yAxis, ...yAxis, field: yField },
    groupBy: stringField(raw.groupBy) ?? defaults.groupBy,
    colorMapping: isRecord(raw.colorMapping) ? raw.colorMapping as unknown as PlotBuilderConfig["colorMapping"] : defaults.colorMapping,
    sizeMapping: isRecord(raw.sizeMapping) ? raw.sizeMapping as unknown as PlotBuilderConfig["sizeMapping"] : defaults.sizeMapping,
    shapeMapping: isRecord(raw.shapeMapping) ? raw.shapeMapping as unknown as PlotBuilderConfig["shapeMapping"] : defaults.shapeMapping,
    title: stringField(raw.title) ?? plot.title ?? defaults.title,
  };
}

function canRenderNativePlot(plot: SavedPlot | null | undefined): boolean {
  return plotRows(plot).length > 0 && plotNativeConfig(plot) !== null;
}

function collectionMemberRefs(plot: SavedPlot | null | undefined): CollectionMemberRef[] {
  if (!plot || !isRecord(plot.config)) return [];
  const config = plot.config;
  const collection = isRecord(config.collection) ? config.collection : null;
  const rawMembers = Array.isArray(collection?.members) ? collection.members : Array.isArray(config.items) ? config.items : [];
  return rawMembers
    .filter(isRecord)
    .map((item) => ({
      plotId: stringField(item.plotId),
      path: stringField(item.path),
      title: stringField(item.title),
      chartType: stringField(item.chartType) ?? stringField(item.chart_type),
    }));
}

function isPlotCollection(plot: SavedPlot | null | undefined): boolean {
  return plot?.chart_type === "plot_collection" || plot?.config?.engine === "collection" || collectionMemberRefs(plot).length > 0;
}

function ThreadPlotFocus({ asset, projectId, branch, plotLarge, onPlotLargeChange, onSelectPlotPoint }: { asset: ThreadAsset; projectId: string; branch: string; plotLarge: boolean; onPlotLargeChange: (large: boolean) => void; onSelectPlotPoint: (runId: string | null, nodeId: string | null) => void }) {
  const plotQuery = useQuery({
    queryKey: ["v5-plot", asset.id],
    queryFn: () => fetchPlot(asset.id),
    enabled: Boolean(asset.id),
  });
  const plot = plotQuery.data;
  const collection = isPlotCollection(plot);
  const membersQuery = useQuery({
    queryKey: ["v5-plot-members", projectId, branch, plot?.id],
    queryFn: () => fetchPlots(projectId, branch, true, 200),
    enabled: Boolean(plot && collection),
  });
  const memberRefs = useMemo(() => collectionMemberRefs(plot), [plot]);
  const memberPlots = useMemo(() => {
    if (!plot || !memberRefs.length) return [];
    const byId = new Map((membersQuery.data ?? []).map((item) => [item.id, item]));
    const byPath = new Map((membersQuery.data ?? []).map((item) => [item.path ?? "", item]));
    return memberRefs
      .map((ref) => (ref.plotId ? byId.get(ref.plotId) : null) ?? (ref.path ? byPath.get(ref.path) : null))
      .filter((item): item is SavedPlot => Boolean(item));
  }, [memberRefs, membersQuery.data, plot]);

  if (plotQuery.isLoading && !plot) {
    return <div className="v5-plot-loading"><Icons.Refresh size={18} />Loading native plot</div>;
  }
  if (!plot) {
    return <PlotImageFallback asset={asset} />;
  }
  if (collection) {
    return <PlotCollectionFocus parent={plot} asset={asset} members={memberPlots} loadingMembers={membersQuery.isLoading} plotLarge={plotLarge} onPlotLargeChange={onPlotLargeChange} onSelectPlotPoint={onSelectPlotPoint} />;
  }
  return <SavedPlotFocusFrame plot={plot} asset={asset} plotLarge={plotLarge} onPlotLargeChange={onPlotLargeChange} onSelectPlotPoint={onSelectPlotPoint} />;
}

function SavedPlotFocusFrame({ plot, asset, mini = false, plotLarge = false, onPlotLargeChange, onSelectPlotPoint }: { plot: SavedPlot; asset: ThreadAsset; mini?: boolean; plotLarge?: boolean; onPlotLargeChange?: (large: boolean) => void; onSelectPlotPoint?: (runId: string | null, nodeId: string | null) => void }) {
  const nativeConfig = plotNativeConfig(plot);
  const nativeRows = plotRows(plot);
  const chartRows = useMemo(() => plotRowsWithNodeFallback(plot, nativeRows), [plot, nativeRows]);
  const canNative = Boolean(nativeConfig && nativeRows.length);
  const hasImage = Boolean(plot.image_url || asset.external);
  const [mode, setMode] = useState<"native" | "image">(canNative ? "native" : "image");

  useEffect(() => {
    if (!canNative) setMode("image");
    else if (!hasImage) setMode("native");
  }, [canNative, hasImage]);

  if (canNative && nativeConfig && mode === "native") {
    const Renderer = getRenderer(nativeConfig.engine);
    return (
      <div className={`v5-plot-frame ${mini ? "is-mini" : ""}`}>
        {!mini ? <PlotToolbar mode={mode} canNative={canNative} hasImage={hasImage} large={plotLarge} onModeChange={setMode} onLargeChange={onPlotLargeChange} /> : null}
        <div className="v5-plot-renderer">
          <Renderer
            data={chartRows}
            config={nativeConfig}
            theme="light"
            mini={mini}
            onPointClick={mini ? undefined : (row) => onSelectPlotPoint?.(plotPointRunId(row), plotPointNodeId(row))}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`v5-plot-frame ${mini ? "is-mini" : ""}`}>
      {!mini ? <PlotToolbar mode={mode} canNative={canNative} hasImage={hasImage} large={plotLarge} onModeChange={setMode} onLargeChange={onPlotLargeChange} /> : null}
      <PlotImageFallback asset={asset} plot={plot} />
    </div>
  );
}

function PlotToolbar({ mode, canNative, hasImage, large, onModeChange, onLargeChange }: { mode: "native" | "image"; canNative: boolean; hasImage: boolean; large: boolean; onModeChange: (mode: "native" | "image") => void; onLargeChange?: (large: boolean) => void }) {
  return (
    <div className="v5-plot-mode">
      {canNative && hasImage ? (
        <span className="v5-plot-mode-group">
          <button className={mode === "native" ? "is-active" : ""} type="button" onClick={() => onModeChange("native")}>Thread</button>
          <button className={mode === "image" ? "is-active" : ""} type="button" onClick={() => onModeChange("image")}>PNG</button>
        </span>
      ) : null}
      <button className={large ? "is-active" : ""} type="button" onClick={() => onLargeChange?.(!large)} title={large ? "Return plot to normal focus size" : "View plot large"}>
        <Icons.Maximize size={12} />
        {large ? "Fit" : "Large"}
      </button>
    </div>
  );
}

function PlotCollectionFocus({ parent, asset, members, loadingMembers, plotLarge, onPlotLargeChange, onSelectPlotPoint }: { parent: SavedPlot; asset: ThreadAsset; members: SavedPlot[]; loadingMembers: boolean; plotLarge: boolean; onPlotLargeChange: (large: boolean) => void; onSelectPlotPoint: (runId: string | null, nodeId: string | null) => void }) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = members.find((plot) => plot.id === selectedId) ?? members[0] ?? null;

  useEffect(() => {
    if (!selectedId && members[0]) setSelectedId(members[0].id);
  }, [members, selectedId]);

  return (
    <div className="v5-plot-collection">
      <div className="v5-plot-collection-main">
        {selected ? <SavedPlotFocusFrame plot={selected} asset={{ ...asset, id: selected.id, label: selected.title, external: selected.image_url ?? asset.external }} plotLarge={plotLarge} onPlotLargeChange={onPlotLargeChange} onSelectPlotPoint={onSelectPlotPoint} /> : <PlotImageFallback asset={asset} plot={parent} />}
      </div>
      <div className="v5-plot-collection-strip">
        {loadingMembers && !members.length ? <span className="v5-muted">Loading collection plots...</span> : null}
        {members.map((plot) => (
          <button key={plot.id} className={`v5-plot-child-card ${selected?.id === plot.id ? "is-active" : ""}`} type="button" onClick={() => setSelectedId(plot.id)}>
            {canRenderNativePlot(plot) ? (
              <SavedPlotFocusFrame plot={plot} asset={{ ...asset, id: plot.id, label: plot.title, external: plot.image_url ?? asset.external }} mini />
            ) : (
              <ArtifactThumb asset={{ ...asset, id: plot.id, label: plot.title, external: plot.image_url ?? asset.external }} />
            )}
            <span>{plot.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function PlotImageFallback({ asset, plot }: { asset: ThreadAsset; plot?: SavedPlot | null }) {
  const imageUrl = plot?.image_url ?? asset.external;
  if (!imageUrl) return <ArtifactThumb asset={asset} large />;
  return (
    <div className="v5-plot-image">
      <img src={imageUrl} alt="" />
    </div>
  );
}

function ArtifactThumb({ asset, large = false }: { asset: ThreadAsset; large?: boolean }) {
  const imageUrl = asset.external && (asset.type === "plot" || asset.preview?.kind === "plot") ? asset.external : null;
  return (
    <span className={`v5-artifact-thumb is-${asset.type} is-${asset.preview?.kind ?? "abstract"} ${imageUrl ? "has-image" : ""} ${large ? "is-large" : ""}`}>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          loading="lazy"
          onError={(event) => {
            event.currentTarget.closest(".v5-artifact-thumb")?.classList.remove("has-image");
            event.currentTarget.remove();
          }}
        />
      ) : null}
      <i /><b /><em />
    </span>
  );
}

function GeometryFocus({ data, projectId, persistedProject, selectedRunId, selectedNodeId, focusedAssetId }: { data: V3WorkbenchData; projectId: string; persistedProject: boolean; selectedRunId: string | null; selectedNodeId: string | null; focusedAssetId: string | null }) {
  const selectionId = selectedNodeId ?? focusedAssetId ?? selectedRunId;
  const rows = flow360RowsForSelection(data, selectedRunId, selectedNodeId, focusedAssetId);
  if (persistedProject && rowsHavePreviewValue(rows, FLOW360_GEOMETRY_KEYS, selectedRunId, selectedNodeId)) {
    return (
      <Flow360ViewerPanel
        projectId={projectId}
        branch={data.branch || "main"}
        rows={rows}
        selectionId={selectionId}
        entityTypes={["geometry", "surface_mesh", "volume_mesh"]}
      />
    );
  }

  const geometry = data.provenance.nodes.find((node) => node.type === "geometry");
  const mesh = data.provenance.nodes.find((node) => node.type === "mesh");
  return (
    <div className="v5-geometry-focus">
      <span className="v5-eyebrow">Geometry</span>
      <h2>{geometry?.label || "Source geometry"}</h2>
      <div className="v5-wireframe"><i /><i /><i /><i /></div>
      <p>{mesh?.label ? `Current mesh lineage: ${mesh.label}` : "Geometry and mesh lineage will appear after import."}</p>
    </div>
  );
}

function FlowfieldPending() {
  return (
    <div className="v5-flow-pending">
      <Icons.Flowfield size={32} />
      <h2>Flowfield pending</h2>
      <p>UVF assets will render here after visualization data is imported or attached to this Thread project.</p>
    </div>
  );
}

function previewSelectionId(selectedNodeId: string | null, selectedRunId: string | null): string | null {
  return selectedNodeId ?? selectedRunId;
}

function GeometryPreviewCard({ data, loading, projectId, branch, persistedProject, selectedRunId, selectedNodeId, available, onOpen }: { data: V3WorkbenchData; loading: boolean; projectId: string; branch: string; persistedProject: boolean; selectedRunId: string | null; selectedNodeId: string | null; available: boolean; onOpen: () => void }) {
  const selectionId = previewSelectionId(selectedNodeId, selectedRunId);
  const rows = flow360RowsForSelection(data, selectedRunId, selectedNodeId);
  const hasFlow360Geometry = rowsHavePreviewValue(rows, FLOW360_GEOMETRY_KEYS, selectedRunId, selectedNodeId);
  const asset = hasFlow360Geometry ? null : geometryPreviewAsset(data.assets, selectedRunId);
  const hasPreview = (persistedProject && hasFlow360Geometry) || Boolean(asset);
  const meta = loading ? "loading" : hasPreview ? (selectedRunId ? `run ${selectedRunId}` : hasFlow360Geometry ? "Flow360 geometry" : "source geometry") : available ? "no 3D preview" : "not imported";

  return (
    <PreviewCard title="Geometry" meta={meta} icon={<Icons.Cube size={15} />} onOpen={onOpen} loading={loading} empty={!hasPreview}>
      {loading ? (
        <PreviewLoadingPanel label="Loading geometry" />
      ) : persistedProject && hasFlow360Geometry ? (
        <Flow360ViewerPanel
          projectId={projectId}
          branch={branch}
          rows={rows}
          compact
          selectionId={selectionId}
          entityTypes={["geometry", "surface_mesh", "volume_mesh"]}
        />
      ) : asset ? (
        <ArtifactThumb asset={asset} />
      ) : (
        <PreviewEmptyPanel
          title={available ? "No 3D preview" : "Geometry not imported"}
          body={available ? "Geometry lineage exists, but no Flow360 geometry ID or saved preview image is attached to this project yet." : "Import or refresh Flow360 geometry to render it here."}
        />
      )}
    </PreviewCard>
  );
}

function FlowfieldPreviewCard({ data, loading, projectId, branch, persistedProject, selectedRunId, selectedNodeId, onOpen }: { data: V3WorkbenchData; loading: boolean; projectId: string; branch: string; persistedProject: boolean; selectedRunId: string | null; selectedNodeId: string | null; onOpen: () => void }) {
  const selectionId = previewSelectionId(selectedNodeId, selectedRunId);
  const rows = flowfieldRowsForSelection(data, selectedRunId, selectedNodeId);
  const hasSelectedCase = Boolean(selectedFlow360CaseId(selectedNodeId, selectedRunId));
  const hasPreview = persistedProject && (rowsHavePreviewValue(rows, ["flow360_case_id", "case_id", "uvf_asset_id", "flowfield_asset_id"], selectedRunId, selectedNodeId) || hasSelectedCase || hasFlowfieldAsset(data.assets));
  const meta = loading ? "loading" : hasPreview ? (selectedRunId ? `run ${selectedRunId}` : "Flow360 available") : "not imported";

  return (
    <PreviewCard title="Flowfield" meta={meta} icon={<Icons.Flowfield size={15} />} onOpen={onOpen} loading={loading} empty={!hasPreview}>
      {loading ? (
        <PreviewLoadingPanel label="Loading flowfield" />
      ) : hasPreview ? (
        <Flow360ViewerPanel
          projectId={projectId}
          branch={branch}
          rows={rows}
          compact
          selectionId={selectionId}
          entityTypes={["case"]}
        />
      ) : (
        <PreviewEmptyPanel
          title="Flowfield not imported"
          body="No linked Flow360 case or UVF asset is attached to the selected Thread rows yet."
        />
      )}
    </PreviewCard>
  );
}

function PreviewCard({ title, meta, icon, onOpen, loading, empty, children }: { title: string; meta: string; icon: ReactNode; onOpen: () => void; loading?: boolean; empty?: boolean; children: ReactNode }) {
  const classes = ["v5-preview-card", empty ? "is-empty" : "", loading ? "is-loading" : ""].filter(Boolean).join(" ");
  return (
    <section className={classes} aria-busy={loading ? "true" : undefined}>
      <div className="v5-preview-title">
        <span>{icon}{title}</span>
        <div className="v5-preview-actions">
          <small>{meta}</small>
          <button className="v5-preview-open-button" type="button" aria-label={`Open ${title}`} title={`Open ${title}`} onClick={onOpen} disabled={loading}>
            <Icons.Maximize size={12} />
          </button>
        </div>
      </div>
      <div className="v5-preview-stage">{children}</div>
    </section>
  );
}

function WorkbenchLoadingPanel({ projectName }: { projectName: string }) {
  return (
    <div className="v5-loading-panel" data-testid="v5-workbench-loading" aria-busy="true">
      <span className="v5-live is-pending"><i />loading data</span>
      <h2>{projectName}</h2>
      <p>Loading runs, artifacts, provenance, and Flow360 links.</p>
      <div className="v5-loading-table" aria-hidden="true">
        {Array.from({ length: 7 }, (_, index) => (
          <span key={index}>
            <i />
            <i />
            <i />
            <i />
          </span>
        ))}
      </div>
    </div>
  );
}

function CardLoadingPanel({ label }: { label: string }) {
  return (
    <div className="v5-card-loading-panel" aria-busy="true">
      <span>{label}</span>
      <div aria-hidden="true">
        <i />
        <i />
        <i />
      </div>
    </div>
  );
}

function PreviewLoadingPanel({ label }: { label: string }) {
  return (
    <div className="v5-preview-loading-panel">
      <span>{label}</span>
      <i />
      <i />
      <i />
    </div>
  );
}

function LoadingStripCard({ title, meta, icon }: { title: string; meta: string; icon: ReactNode }) {
  return (
    <section className="v5-hint-card v5-loading-card" aria-busy="true">
      <span>{icon}{title}</span>
      <div className="v5-loading-card-lines" aria-hidden="true">
        <i />
        <i />
        <i />
      </div>
      <small>{meta}</small>
    </section>
  );
}

function PreviewEmptyPanel({ title, body }: { title: string; body: string }) {
  return (
    <div className="v5-preview-empty-panel">
      <strong>{title}</strong>
      <span>{body}</span>
    </div>
  );
}

function NotebookStrip({ selectedRunId, noteCount, onOpen }: { selectedRunId: string | null; noteCount: number; onOpen: () => void }) {
  return <HintCard title="Spool" meta={selectedRunId ? `${noteCount} notes - run context` : `${noteCount} notes`} icon={<Icons.MdNote size={15} />} onClick={onOpen} />;
}

/** Strip card for the condition-pivot table (FXC-8608). Opens the Data view. */
function ConditionPivotStripCard({ loading, rowCount, onOpen }: { loading: boolean; rowCount: number; onOpen: () => void }) {
  if (loading) {
    return <LoadingStripCard title="Condition pivot" meta="loading run table" icon={<Icons.GridIcon size={13} />} />;
  }
  return (
    <HintCard
      title="Condition pivot"
      meta={rowCount > 0 ? `${rowCount} runs · α / β / RPM` : "no runs yet"}
      icon={<Icons.GridIcon size={13} />}
      onClick={onOpen}
    />
  );
}

function HintCard({ title, meta, icon, onClick }: { title: string; meta: string; icon: ReactNode; onClick: () => void }) {
  return <button className="v5-hint-card" type="button" onClick={onClick}><span>{icon}{title}</span><small>{meta}</small></button>;
}

function EmptyProjectState({ projectName, persistedProject, hasGeometry, reason, onOpenFlow360 }: { projectName: string; persistedProject: boolean; hasGeometry: boolean; reason?: string | null; onOpenFlow360?: () => void }) {
  const hasIssue = Boolean(reason);
  return (
    <div className={`v5-empty-project ${hasIssue ? "has-issue" : ""}`}>
      <span className={`v5-live ${hasIssue ? "is-pending" : "is-connected"}`}><i />{hasIssue ? "data source needs attention" : persistedProject ? "live project connected" : "fixture ready"}</span>
      <h2>{projectName}</h2>
      <p>{hasIssue ? reason : hasGeometry ? "Geometry and mesh lineage are stored, but this Thread project does not have imported run rows yet. Stored runs appear here from the Thread database; Flow360 is only needed to import, refresh, or submit new work." : "This Thread project does not have imported run rows yet. Stored runs appear here from the Thread database; Flow360 is only needed to import, refresh, or submit new work."}</p>
      {onOpenFlow360 ? <button className="v5-primary-button" type="button" onClick={onOpenFlow360}><Icons.Flowfield size={14} />Import or refresh Flow360</button> : null}
      <div><span>table</span><span>artifacts</span><span>DAG</span><span>flowfield</span></div>
    </div>
  );
}

function EmptyShelf() {
  return <div className="v5-empty-shelf"><strong>No artifacts</strong><p>Artifacts appear here as real runs publish plots, meshes, logs, or UVF visualizations.</p></div>;
}

function Stat({ label, value }: { label: string; value: number }) {
  return <div className="v5-stat"><strong>{value.toLocaleString()}</strong><span>{label}</span></div>;
}

function projectDateLabel(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Created date unavailable";
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(date);
}

function projectMetaLabel(project: Project): string {
  const links = project.flow360_links ?? [];
  if (links.length === 1) return "1 Flow360 project";
  if (links.length > 1) return `${links.length} Flow360 projects`;
  const metadata = project.metadata ?? {};
  if (metadata.local_only === true) return "Local fixture";
  if (typeof metadata.fixture === "string") return `${metadata.fixture} fixture`;
  if (Array.isArray(metadata.flow360_project_ids) && metadata.flow360_project_ids.length) return `${metadata.flow360_project_ids.length} Flow360 folders`;
  if (typeof metadata.flow360_project_id === "string") return "Flow360 linked";
  return "Thread project";
}

function projectActivityLabel(project: Project): string {
  const summary = project.activity_summary;
  if (!summary) return projectMetaLabel(project);
  if (summary.last_activity_at) return `Active ${projectDateLabel(summary.last_activity_at)}`;
  if (summary.node_count > 0) return formatProjectActivityCount(summary.node_count, "node");
  return projectMetaLabel(project);
}

function flow360ProjectNames(project: Project): string {
  const links = project.flow360_links ?? [];
  const names = links
    .map((link) => link.flow360_project_name || link.flow360_project_id)
    .filter(Boolean);
  if (names.length === 0) return projectMetaLabel(project);
  const first = names[0]!;
  const extra = names.length > 1 ? ` + ${names.length - 1} more` : "";
  const cases = Math.max(
    project.activity_summary?.flow360_case_count ?? 0,
    links.reduce((total, link) => total + (link.case_count || 0), 0),
  );
  const caseLabel = cases > 0 ? ` · ${cases} case${cases === 1 ? "" : "s"}` : "";
  return `Flow360: ${first}${extra}${caseLabel}`;
}

function groupAssets(assets: ThreadAsset[]): Array<[string, ThreadAsset[]]> {
  const grouped = new Map<string, ThreadAsset[]>();
  for (const asset of assets) {
    const key = asset.preview?.kind ?? asset.type;
    grouped.set(key, [...(grouped.get(key) ?? []), asset]);
  }
  return [...grouped.entries()];
}

function kindLabel(kind: string): string {
  return kind.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function assetSearchText(asset: ThreadAsset): string {
  return `${asset.label} ${asset.sub} ${asset.summary ?? ""} ${asset.preview?.kind ?? ""}`.toLowerCase();
}

function isGeometryPreviewAsset(asset: ThreadAsset): boolean {
  if (!asset.external || (asset.type !== "plot" && asset.preview?.kind !== "plot")) return false;
  return /\b(geometry|mesh|planform|airframe|shape)\b/.test(assetSearchText(asset));
}

function geometryPreviewAsset(assets: ThreadAsset[], selectedRunId: string | null): ThreadAsset | null {
  const candidates = assets.filter(isGeometryPreviewAsset);
  return candidates.find((asset) => assetLinkedToRun(asset, selectedRunId)) ?? candidates[0] ?? null;
}

function assetLinkedToRun(asset: ThreadAsset, selectedRunId: string | null): boolean {
  if (!selectedRunId) return false;
  return Boolean(asset.refs.rows?.includes(selectedRunId) || asset.refs.cells?.some((cell) => cell.runId === selectedRunId));
}

function valueFor(row: ThreadRow, keys: string[]): unknown {
  for (const key of keys) {
    if (row[key] !== undefined) return row[key];
  }
  return null;
}
